/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
/* eslint-disable max-len */
/* eslint-disable id-length */
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from 'react-router-dom';
import UseAxios from '../../../Api/http/http';
import { MODULE_NAMES } from '../../../Constants/modules';
import {
  createReasonReport,
  createProjectCsat,
  deleteProjectCsat,
  createProjectProgress,
  deleteProjectProgress,
  getPrimaryReasons,
  getReasonReports,
  getSecondaryReasons,
  updateReasonReport,
  updateProjectCsat,
  updateProjectDetails,
  deleteProjectDetails,
  updateProjectProgress,
  createProjectUnit,
  updateProjectUnit,
  deleteProjectUnit,
} from '../../../Api/request/projectDetails';
import { useAuth } from '../../../Contexts/AuthContext';
import { useSnackbar } from '../../../Components/Snackbar/snackBarProvider';
import './expandableSections.scss';
import exportToExcel from '../../../Utils/exportToExcel';
import CsatSection from './components/sections/csatSection';
import ProgressSection from './components/sections/progressSection';
import EffortsSection from './components/sections/effortsSection';
import ProjectUnitsSection from './components/sections/projectUnitsSection';
import ReasonsRemarksSection from './components/sections/reasonsRemarksSection';
import AuditSection from './components/sections/auditSection';
import RiskRegisterSection from './components/sections/riskRegisterSection';
import MilestoneSection from './components/sections/milestoneSection';
import ResourceCountSection from './components/sections/resourceCountSection';
import ResourceDetailsSection from './components/sections/resourceDetailsSection';
import TbhResourcesSection from './components/sections/tbhResourcesSection';
import ProjectDetailsSectionModals from './components/projectDetailsSectionModals';
import { Dialog } from 'primereact/dialog';
import CustomDatePicker from './customDatePicker';
import dayjs from 'dayjs';
import { createRequest } from '../../../Api/request/staffingRequest';

const RISK_STATUS_RING_COLORS = {
  open: '#2f80ed',
  closed: '#27ae60',
  escalated: '#eb5757',
};
const RESOURCE_RING_COLORS = {
  open: '#6fd3e6',
  closed: '#f0b08a',
  escalated: '#9a92f7',
};
const SHOW_REASONS_REMARKS_SECTION = false;
const EMPTY_RING_COLOR = '#dfe3eb';
const MONTH_INDEX_MAP = {
  Jan: 0,
  Feb: 1,
  Mar: 2,
  Apr: 3,
  May: 4,
  Jun: 5,
  Jul: 6,
  Aug: 7,
  Sep: 8,
  Oct: 9,
  Nov: 10,
  Dec: 11,
};
const MONTH_NAMES_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const readFirstPresent = (...values) => {
  for (const value of values) {
    if (value !== null && value !== undefined && value !== '') return value;
  }
  return '';
};
const toExportValue = (value, fallback = '') => (value === null || value === undefined ? fallback : value);
const normalizeUiDateToDdMmmYyyy = (value) => {
  const text = String(value || '').trim();
  if (!text) return '';
  const parts = text.split(',');
  return (parts.length > 1 ? parts.slice(1).join(',') : parts[0]).trim();
};
const getAriaSort = (activeKey, sortKey, direction) => {
  if (activeKey !== sortKey) return 'none';
  return direction === 'desc' ? 'descending' : 'ascending';
};
const getRiskSummaryType = (activeRiskTabKey = '') => {
  if (activeRiskTabKey.includes('issue')) return 'issue';
  if (activeRiskTabKey.includes('infosec')) return 'infosec';
  return 'risk';
};
const getRiskRowsBySummaryType = (summaryType, { riskRows, issueRows, infoSecRows }) => {
  if (summaryType === 'issue') return issueRows;
  if (summaryType === 'infosec') return infoSecRows;
  return riskRows;
};
const isYyyyMmDdDate = (value) => /^\d{4}-\d{2}-\d{2}$/.test(String(value || ''));
const normalizeDateForApi = (value) => {
  if (!value) return '';
  if (dayjs.isDayjs(value)) return value.format('YYYY-MM-DD');
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
};

const toOptionalPositiveNumber = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : undefined;
};

const formatLegacyAllocationDate = (value) => {
  const normalized = normalizeDateForApi(value);
  return normalized ? dayjs(normalized).format('DD-MMM-YYYY') : '-';
};

const buildLegacyAllocationChangeRemark = ({
  employeeId,
  name,
  allocation,
  startDate,
  endDate,
  comments,
  isRollOff,
}) => {
  const empRef = employeeId || name || 'resource';
  const safeAllocation = Number.isFinite(Number(allocation)) ? Number(allocation) : 0;
  const trimmedComments = String(comments || '').trim();

  if (isRollOff) {
    const base = `(Roll off request for ${empRef}- ${name || ''}, with existing allocation ${safeAllocation}% and E.D ${formatLegacyAllocationDate(endDate)})`;
    return trimmedComments ? `${base}. ${trimmedComments}.` : `${base}.`;
  }

  const base = `(Change for ${empRef}-${name || ''} with existing allocation ${safeAllocation}%, S.D ${formatLegacyAllocationDate(startDate)} and E.D ${formatLegacyAllocationDate(endDate)})`;
  return trimmedComments ? `${base} ${trimmedComments}.` : `${base}.`;
};

const getVisibleRiskRowsBySummaryType = (summaryType, { riskRows, infoSecRows }) => (
  summaryType === 'infosec' ? infoSecRows : riskRows
);
const applySortDirection = (rows, direction) => (direction === 'asc' ? rows : rows.reverse());
const getNextSortState = (previous, key) => (
  previous.key === key
    ? { key, direction: previous.direction === 'asc' ? 'desc' : 'asc' }
    : { key, direction: 'asc' }
);
const getSortClassName = (sortState, key) => {
  if (sortState.key !== key) return '';
  return sortState.direction === 'asc' ? 'sorted-asc' : 'sorted-desc';
};
const getResourceStatusPriority = (status = '') => {
  const normalizedStatus = String(status || '').trim()
    .toLowerCase();

  if (normalizedStatus === 'active') return 0;
  if (normalizedStatus === 'shadow') return 0;
  if (normalizedStatus === 'moved out') return 2;
  return 1;
};

const hasSuperAdminRole = (userProfile = {}) => {
  const superAdminFlag = userProfile?.superAdmin ?? userProfile?.super_admin;
  if (Number(superAdminFlag) === 1 || superAdminFlag === true) {
    return true;
  }
  const hasSuperAdmin = userProfile?.roleDetails?.some((role) => {
    const roleName = String(role?.roleName || role?.role_name || '').toLowerCase();
    const roleId = Number(role?.roleId || role?.role_id);
    return roleName === 'super admin' || roleId === 1;
  });
  return Boolean(hasSuperAdmin);
};

const getRiskSummaryConfig = (summaryType, activeSummary) => {
  if (summaryType === 'issue') {
    return {
      total: activeSummary.open + activeSummary.escalated + activeSummary.closed,
      breakdown: [
        { label: 'Open', value: activeSummary.open, dot: 'open' },
        { label: 'Closed', value: activeSummary.closed, dot: 'closed' },
        { label: 'Escalated', value: activeSummary.escalated, dot: 'escalated' },
      ],
      cards: [
        { label: 'Critical priority', value: activeSummary.high, color: 'red', icon: '⚠' },
        { label: 'Medium priority', value: activeSummary.medium, color: 'yellow', icon: '⚠' },
        { label: 'Low priority', value: activeSummary.low, color: 'green', icon: '✓' },
        { label: 'Overdue open issues', value: activeSummary.overdueOpen, color: 'orange', icon: '⏰' },
      ],
    };
  }

  const isInfoSec = summaryType === 'infosec';

  return {
    total: isInfoSec
      ? activeSummary.total
      : activeSummary.open + activeSummary.closed + activeSummary.escalated,
    breakdown: [
      { label: 'Open', value: activeSummary.open, dot: 'open' },
      { label: 'Closed', value: activeSummary.closed, dot: 'closed' },
      {
        label: isInfoSec ? 'Transferred' : 'Escalated',
        value: activeSummary.escalated,
        dot: 'escalated',
      },
    ],
    cards: [
      {
        label: isInfoSec ? 'High-Risk Issues' : 'High-Risk Score',
        value: activeSummary.high,
        color: 'red',
        icon: '⚠',
      },
      {
        label: isInfoSec ? 'Medium-Risk Issues' : 'Medium-Risk Score',
        value: activeSummary.medium,
        color: 'yellow',
        icon: '⚠',
      },
      {
        label: isInfoSec ? 'Low-Risk Issues' : 'Low-Risk Score',
        value: activeSummary.low,
        color: 'green',
        icon: '✓',
      },
      {
        label: isInfoSec ? 'Overdue open issues' : 'Overdue open risks',
        value: activeSummary.overdueOpen,
        color: 'orange',
        icon: '⏰',
      },
    ],
  };
};

const buildRingSeries = (values, colors, emptyColor = EMPTY_RING_COLOR) => {
  const entries = values.map((value, index) => ({
    value: Math.max(0, Number(value) || 0),
    color: colors[index % colors.length],
    index,
  }));

  const filtered = entries.filter((entry) => entry.value > 0);
  const total = filtered.reduce((sum, entry) => sum + entry.value, 0);
  const segments = filtered.length;

  if (!segments || total <= 0) {
    return [
      {
        id: 'empty-ring',
        value: 1,
        color: emptyColor,
      },
    ];
  }
  return filtered.map((entry) => ({
    id: `segment-${entry.index}`,
    value: entry.value,
    color: entry.color,
  }));
};

const ExpandableSections = ({
  expandedSections,
  onToggleSection,
  // CSAT data
  csatData = {},
  csatTableData = [],
  csatCustomers = [],
  csatCustomersLoading = false,
  csatCustomersError = null,
  lazyLoadCsat = null,
  // Progress data
  progressData = [],
  progressLoading = false,
  // Efforts data
  effortsData = {},
  effortsLoading = false,
  projectUnitsData = { isApplicable: false, rows: [], resourceCategoryOptions: [] },
  projectUnitsLoading = false,
  // Audit data
  auditScoreData = {},
  auditLoading = false,
  // Risk register data
  riskRegisterData = {},
  riskLoading = false,
  lazyLoadRisk = null,
  // Milestone data
  milestoneData = {},
  // Resource data
  resourceCountData = [],
  resourceAllocationData = {},
  resourceLoading = false,
  tbhResources = [],
  tbhLoading = false,
  projectTypeVariant = '',
  projectSeqId = null,
  projectId = null,
  projectName = '',
  projectVertical = '',
  onRefreshData = null,
  resourceTypeOptions = [],
  projectStatus = '',
  projectData = {},
}) => { // NOSONAR
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { snackbarOpen } = useSnackbar();
  const [isActionInProgress, setIsActionInProgress] = useState(false);
  const [isAddTbhResourceOpen, setIsAddTbhResourceOpen] = useState(false);
  const [editingTbhAllocation, setEditingTbhAllocation] = useState(null);
  const employeeId = userProfile?.employeeId || userProfile?.employee_id || '';
  const accessToken = userProfile?.access_token || '';
  const isSuperAdmin = hasSuperAdminRole(userProfile);
  const permissions = userProfile?.permissions || {};
  const getPermissionActions = (moduleName) => (
    Array.isArray(permissions?.[moduleName]) ? permissions[moduleName] : []
  );
  const hasPermissionAction = (moduleName, action) => getPermissionActions(moduleName).includes(action);
  const canViewProjectProgress = hasPermissionAction(MODULE_NAMES.DELIVERY_PROJECT_PROGRESS, 'read');
  const canCreateProjectProgress = hasPermissionAction(MODULE_NAMES.DELIVERY_PROJECT_PROGRESS, 'create');
  const canUpdateProjectProgress = hasPermissionAction(MODULE_NAMES.DELIVERY_PROJECT_PROGRESS, 'update');
  const canDeleteProjectProgress = hasPermissionAction(MODULE_NAMES.DELIVERY_PROJECT_PROGRESS, 'delete');
  const canShowProjectProgressActions = canUpdateProjectProgress || canDeleteProjectProgress;
  const canViewEffortEstimation = hasPermissionAction(MODULE_NAMES.DELIVERY_PROJECT_OVERVIEW_EFFORT_ESTIMATION, 'read');
  const canCreateEffortEstimation = hasPermissionAction(MODULE_NAMES.DELIVERY_PROJECT_OVERVIEW_EFFORT_ESTIMATION, 'create');
  const canUpdateEffortEstimation = hasPermissionAction(MODULE_NAMES.DELIVERY_PROJECT_OVERVIEW_EFFORT_ESTIMATION, 'update');
  const canDeleteEffortEstimation = hasPermissionAction(MODULE_NAMES.DELIVERY_PROJECT_OVERVIEW_EFFORT_ESTIMATION, 'delete');
  const canViewRisk = hasPermissionAction(MODULE_NAMES.DELIVERY_PROJECT_OVERVIEW_RISK, 'read');
  const canCreateRisk = hasPermissionAction(MODULE_NAMES.DELIVERY_PROJECT_OVERVIEW_RISK, 'create');
  const canUpdateRisk = hasPermissionAction(MODULE_NAMES.DELIVERY_PROJECT_OVERVIEW_RISK, 'update');
  const canDeleteRisk = hasPermissionAction(MODULE_NAMES.DELIVERY_PROJECT_OVERVIEW_RISK, 'delete');
  const canViewIssue = hasPermissionAction(MODULE_NAMES.DELIVERY_PROJECT_OVERVIEW_ISSUE, 'read');
  const canCreateIssue = hasPermissionAction(MODULE_NAMES.DELIVERY_PROJECT_OVERVIEW_ISSUE, 'create');
  const canUpdateIssue = hasPermissionAction(MODULE_NAMES.DELIVERY_PROJECT_OVERVIEW_ISSUE, 'update');
  const canDeleteIssue = hasPermissionAction(MODULE_NAMES.DELIVERY_PROJECT_OVERVIEW_ISSUE, 'delete');
  const canViewInfoSecRisk = hasPermissionAction(MODULE_NAMES.DELIVERY_PROJECT_OVERVIEW_INFOSEC_RISK, 'read');
  const canCreateInfoSecRisk = hasPermissionAction(MODULE_NAMES.DELIVERY_PROJECT_OVERVIEW_INFOSEC_RISK, 'create');
  const canUpdateInfoSecRisk = hasPermissionAction(MODULE_NAMES.DELIVERY_PROJECT_OVERVIEW_INFOSEC_RISK, 'update');
  const canDeleteInfoSecRisk = hasPermissionAction(MODULE_NAMES.DELIVERY_PROJECT_OVERVIEW_INFOSEC_RISK, 'delete');
  const canViewMisRiskRegister = hasPermissionAction(MODULE_NAMES.MIS_RISK_REGISTER, 'read');
  const canViewAnyRiskRegister = canViewRisk || canViewIssue || canViewInfoSecRisk;
  const riskVisibleTabs = useMemo(() => ([
    canViewRisk ? 'Risk' : null,
    canViewIssue ? 'Issue' : null,
    canViewInfoSecRisk ? 'InfoSec Risk' : null,
  ].filter(Boolean)), [canViewRisk, canViewIssue, canViewInfoSecRisk]);
  const canViewCsat = hasPermissionAction(MODULE_NAMES.DELIVERY_PROJECT_OVERVIEW_CSAT, 'read');
  const canViewMisCsat = hasPermissionAction(MODULE_NAMES.MIS_CSAT_HISTORY, 'read');
  const canCreateCsat = hasPermissionAction(MODULE_NAMES.DELIVERY_PROJECT_OVERVIEW_CSAT, 'create');
  const canUpdateCsat = hasPermissionAction(MODULE_NAMES.DELIVERY_PROJECT_OVERVIEW_CSAT, 'update');
  const canDeleteCsat = hasPermissionAction(MODULE_NAMES.DELIVERY_PROJECT_OVERVIEW_CSAT, 'delete');
  const canShowCsatActions = canUpdateCsat || canDeleteCsat;
  const canViewCsatCustomerDetails = isSuperAdmin || hasPermissionAction(MODULE_NAMES.MIS_CSAT_CUSTOMER_DETAILS, 'read');
  const canCreateCsatCustomerDetails = isSuperAdmin || hasPermissionAction(MODULE_NAMES.MIS_CSAT_CUSTOMER_DETAILS, 'create');
  const canViewAuditScore = hasPermissionAction(MODULE_NAMES.DELIVERY_AUDIT_SCORE, 'read');
  const canCreateAuditScore = hasPermissionAction(MODULE_NAMES.DELIVERY_AUDIT_SCORE, 'create');
  const canUpdateAuditScore = hasPermissionAction(MODULE_NAMES.DELIVERY_AUDIT_SCORE, 'update');
  const canDeleteAuditScore = hasPermissionAction(MODULE_NAMES.DELIVERY_AUDIT_SCORE, 'delete');
  const canShowAuditActions = canUpdateAuditScore || canDeleteAuditScore;
  const canViewResourceAllocationCount = isSuperAdmin || hasPermissionAction(
    MODULE_NAMES.DELIVERY_RESOURCE_ALLOCATION_COUNT,
    'read',
  );
  const canViewFinanceMilestone = isSuperAdmin || hasPermissionAction(
    MODULE_NAMES.FINANCE_MILESTONE,
    'read',
  );
  const canViewResourceAllocationDetails = isSuperAdmin || hasPermissionAction(
    MODULE_NAMES.DELIVERY_RESOURCE_ALLOCATION_DETAILS,
    'read',
  );
  const canCreateResourceAllocationDetails = isSuperAdmin || hasPermissionAction(
    MODULE_NAMES.DELIVERY_RESOURCE_ALLOCATION_DETAILS,
    'create',
  );
  const canUpdateResourceAllocationDetails = isSuperAdmin || hasPermissionAction(
    MODULE_NAMES.DELIVERY_RESOURCE_ALLOCATION_DETAILS,
    'update',
  );
  const canDeleteResourceAllocationDetails = isSuperAdmin || hasPermissionAction(
    MODULE_NAMES.DELIVERY_RESOURCE_ALLOCATION_DETAILS,
    'delete',
  );
  const canViewProjectUnit = isSuperAdmin || hasPermissionAction(
    MODULE_NAMES.DELIVERY_PROJECT_UNIT,
    'read',
  );
  const canCreateProjectUnit = isSuperAdmin || hasPermissionAction(
    MODULE_NAMES.DELIVERY_PROJECT_UNIT,
    'create',
  );
  const canUpdateProjectUnit = isSuperAdmin || hasPermissionAction(
    MODULE_NAMES.DELIVERY_PROJECT_UNIT,
    'update',
  );
  const canDeleteProjectUnit = isSuperAdmin || hasPermissionAction(
    MODULE_NAMES.DELIVERY_PROJECT_UNIT,
    'delete',
  );
  const canViewProjectUnitAmount = isSuperAdmin || hasPermissionAction(
    MODULE_NAMES.DELIVERY_PROJECT_UNIT_AMOUNT,
    'read',
  );
  const canViewResourceLoadingPlan = isSuperAdmin || hasPermissionAction(
    MODULE_NAMES.DELIVERY_RESOURCE_LOADING_PLAN,
    'read',
  );
  const canViewChangeRequest = isSuperAdmin
    || hasPermissionAction(MODULE_NAMES.PROJECT_CHANGE_REQUEST, 'read')
    || hasPermissionAction(MODULE_NAMES.PROJECT_CHANGE_REQUEST, 'view');
  // Modal states
  const [modalState, setModalState] = useState({
    // CSAT modals
    csatEdit: { isOpen: false, data: null },
    csatDelete: { isOpen: false, data: null },

    // Progress modals
    progressEdit: { isOpen: false, data: null },
    progressDelete: { isOpen: false, data: null },

    // Efforts modals
    effortEdit: { isOpen: false, data: null },
    effortDelete: { isOpen: false, data: null },

    // Project unit modals
    projectUnitEdit: { isOpen: false, data: null },
    projectUnitDelete: { isOpen: false, data: null },

    // Audit modals
    auditEdit: { isOpen: false, data: null },
    auditDelete: { isOpen: false, data: null },

    // Risk modals
    riskEdit: { isOpen: false, data: null },
    riskDelete: { isOpen: false, data: null },
    issueEdit: { isOpen: false, data: null },
    issueDelete: { isOpen: false, data: null },
    infoSecEdit: { isOpen: false, data: null },
    infoSecDelete: { isOpen: false, data: null },

    // Invoice modals
    invoiceAdd: { isOpen: false, data: null },
    invoiceEdit: { isOpen: false, data: null },
    invoiceDelete: { isOpen: false, data: null },

    // Collection modals
    collectionAdd: { isOpen: false, data: null },
    collectionEdit: { isOpen: false, data: null },
    collectionDelete: { isOpen: false, data: null },
  });

  // Utility to check if a date string is valid
  const getValidDayjs = (dateStr) => {
    if (!dateStr || dateStr === '-' || !dayjs(dateStr).isValid()) return null;
    return dayjs(dateStr);
  };

  const getCurrentDateFormatted = () => dayjs().startOf('day');


  const [primaryReasonOptions, setPrimaryReasonOptions] = useState([]);
  const [secondaryReasonOptions, setSecondaryReasonOptions] = useState([]);
  const [reasonsRemarksForm, setReasonsRemarksForm] = useState({
    reportId: '',
    primaryReasonId: '',
    secondaryReasonId: '',
    remarks: '',
  });
  const [isReasonsLoading, setIsReasonsLoading] = useState(false);
  const [isReasonsSaving, setIsReasonsSaving] = useState(false);
  const [editResourceData, setEditResourceData] = useState(null);
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
  const today = dayjs().startOf('day');

  const isMovedOut
    = String(editResourceData?.status || '')
      .trim()
      .toLowerCase() === 'moved out';

  const isRollOff = editResourceData?.isRollOff || false;

  const endDateDayjs = getValidDayjs(editResourceData?.endDate);

  const isEndDatePast = endDateDayjs
    ? endDateDayjs.startOf('day').isBefore(today)
    : false;

  // Fully locked only after roll-off + expired end date
  const isFullyLocked
    = isMovedOut
    && isRollOff
    && isEndDatePast;

  // Partial lock starts ONLY after checkbox checked
  const isPartiallyLocked
    = isMovedOut
    && isRollOff
    && !isEndDatePast;

  // Field controls
  const disableAllocationField
    = isFullyLocked || isRollOff;

  const disableStartDateField
    = isFullyLocked || isRollOff;

  const disableEndDateField
    = isFullyLocked;

  const disableCommentsField
    = isFullyLocked;

  const getErrorMessage = (err, fallbackMessage) => {
    const status = err?.response?.status;
    const apiMessage = err?.response?.data?.message;
    if (apiMessage) return apiMessage;
    if (status === 400) return 'Invalid request. Please check all required fields.';
    if (status === 401) return 'Your session has expired. Please login again.';
    if (status === 404 || status === 441) return 'No data found for the requested record.';
    if (status === 500) return 'Something went wrong on server. Please try again.';
    return err?.message || fallbackMessage;
  };

  const getDeletePayload = (rowData = {}, entityType = '') => ({
    row_id: rowData?.row_id ?? rowData?.id,
    entity_type: entityType,
    payload: { userInfo: { employeeId } },
  });

  const getUpdatePayload = (formData = {}, entityType = '') => {
    const sanitizedData = Object.entries(formData).reduce((acc, [key, value]) => {
      if (['id', 'row_id', 'entity_type', 'payload'].includes(key)) return acc;
      acc[key] = value;
      return acc;
    }, {});
    return {
      ...sanitizedData,
      row_id: formData?.row_id ?? formData?.id,
      entity_type: entityType,
      payload: { userInfo: { employeeId } },
    };
  };

  const assertApiSuccess = (response, fallbackMessage) => {
    const responseData = response?.data;
    if (!responseData || responseData?.name === 'AxiosError' || responseData?.isAxiosError) {
      throw responseData || new Error(fallbackMessage);
    }
    if (typeof responseData === 'object' && responseData !== null && responseData.success === false) {
      throw new Error(responseData.message || fallbackMessage);
    }
  };

  const resetReasonsRemarksForm = useCallback(() => {
    setReasonsRemarksForm({
      reportId: '',
      primaryReasonId: '',
      secondaryReasonId: '',
      remarks: '',
    });
  }, []);

  const normalizeReasonsRemarksRecord = useCallback((record = {}) => ({
    reportId: String(record?.reportId || ''),
    primaryReasonId: String(record?.primaryReasonId || ''),
    secondaryReasonId: String(record?.secReasonId || ''),
    remarks: String(record?.remarks || ''),
  }), []);

  const loadReasonsRemarksData = useCallback(async () => {
    if (!projectId) {
      setIsReasonsLoading(false);
      setPrimaryReasonOptions([]);
      setSecondaryReasonOptions([]);
      resetReasonsRemarksForm();
      return;
    }

    setIsReasonsLoading(true);

    try {
      const [primaryResponse, secondaryResponse, reportResponse] = await Promise.all([
        UseAxios(getPrimaryReasons(`primary-${projectId}`)),
        UseAxios(getSecondaryReasons(`secondary-${projectId}`)),
        UseAxios(getReasonReports({ projectId }, `report-${projectId}`)),
      ]);

      assertApiSuccess(primaryResponse, 'Failed to load primary reasons.');
      assertApiSuccess(secondaryResponse, 'Failed to load secondary reasons.');

      setPrimaryReasonOptions(primaryResponse?.data?.primaryReasonsList || []);
      setSecondaryReasonOptions(secondaryResponse?.data?.secondaryReasonsList || []);

      const reportError = reportResponse?.data;
      if (reportError?.name === 'AxiosError' || reportError?.isAxiosError) {
        const reportStatus = reportError?.response?.status;
        if (reportStatus === 404 || reportStatus === 441) {
          resetReasonsRemarksForm();
          return;
        }
        throw reportError;
      }

      const reasonReportList = reportResponse?.data?.reasonReportList || [];
      const existingRecord = reasonReportList.find(
        (record) => String(record?.projectId || '') === String(projectId),
      );
      if (existingRecord) {
        setReasonsRemarksForm(normalizeReasonsRemarksRecord(existingRecord));
      } else {
        resetReasonsRemarksForm();
      }
    } catch (err) {
      setPrimaryReasonOptions([]);
      setSecondaryReasonOptions([]);
      resetReasonsRemarksForm();
      snackbarOpen(getErrorMessage(err, 'Failed to load reasons and remarks.'), 'error');
    } finally {
      setIsReasonsLoading(false);
    }
  }, [
    normalizeReasonsRemarksRecord,
    projectId,
    resetReasonsRemarksForm,
    snackbarOpen,
  ]);

  useEffect(() => {
    if (!SHOW_REASONS_REMARKS_SECTION || !canViewEffortEstimation) return;
    loadReasonsRemarksData();
  }, [canViewEffortEstimation, loadReasonsRemarksData]);

  const handleReasonsRemarksChange = useCallback((field, value) => {
    setReasonsRemarksForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleReasonsRemarksSave = useCallback(async () => {
    if (!projectId) {
      snackbarOpen('Project ID is missing for reasons and remarks.', 'error');
      return;
    }

    if (isReasonsSaving) return;

    setIsReasonsSaving(true);

    try {
      const payload = {
        projectId,
        primaryReasonId: reasonsRemarksForm.primaryReasonId || null,
        secReasonId: reasonsRemarksForm.secondaryReasonId || null,
        remarks: reasonsRemarksForm.remarks || '',
      };

      const response = reasonsRemarksForm.reportId
        ? await UseAxios(updateReasonReport(reasonsRemarksForm.reportId, payload))
        : await UseAxios(createReasonReport(payload));

      assertApiSuccess(
        response,
        reasonsRemarksForm.reportId
          ? 'Failed to update reasons and remarks.'
          : 'Failed to save reasons and remarks.',
      );

      const savedRecord = response?.data?.reasonReport;
      if (savedRecord) {
        setReasonsRemarksForm(normalizeReasonsRemarksRecord(savedRecord));
      }

      snackbarOpen(
        reasonsRemarksForm.reportId
          ? 'Reasons and remarks updated successfully.'
          : 'Reasons and remarks saved successfully.',
        'success',
      );
    } catch (err) {
      snackbarOpen(getErrorMessage(err, 'Failed to save reasons and remarks.'), 'error');
    } finally {
      setIsReasonsSaving(false);
    }
  }, [
    isReasonsSaving,
    normalizeReasonsRemarksRecord,
    projectId,
    reasonsRemarksForm,
    snackbarOpen,
  ]);

  // Safe data access with defaults
  const csatScore = csatData?.score || 0;
  const csatDate = csatData?.date || '-';
  const csatOverallTotal = csatData?.overall?.total || 0;
  const csatOverallAverage = csatData?.overall?.average || 0;
  const csatCurrentYearTotal = csatData?.currentYear?.total || 0;
  const csatCurrentYearAverage = csatData?.currentYear?.average || 0;

  // ========== CSAT SORTING ==========
  const [csatSort, setCsatSort] = useState({ key: null, direction: 'asc' });
  const csatCustomerNameById = useMemo(() => {
    const map = new Map();
    (Array.isArray(csatCustomers) ? csatCustomers : []).forEach((customer) => {
      const id = customer?.customer_id;
      const name = customer?.customer_name;
      if (id === null || id === undefined || !name) return;
      map.set(String(id), String(name));
    });
    return map;
  }, [csatCustomers]);

  const resolveCsatCustomerName = (row) => {
    const id = row?.customerId;
    if (id !== null && id !== undefined && csatCustomerNameById.has(String(id))) {
      return csatCustomerNameById.get(String(id));
    }
    return row?.customer || '';
  };

  const csatSortedData = useMemo(() => {
    if (!csatSort.key) return csatTableData;
    const sorted = [...csatTableData].sort((firstRow, secondRow) => {
      const firstValue = csatSort.key === 'customer'
        ? resolveCsatCustomerName(firstRow)
        : firstRow[csatSort.key];
      const secondValue = csatSort.key === 'customer'
        ? resolveCsatCustomerName(secondRow)
        : secondRow[csatSort.key];
      if (typeof firstValue === 'number' && typeof secondValue === 'number') {
        return firstValue - secondValue;
      }
      return String(firstValue).localeCompare(String(secondValue));
    });
    return applySortDirection(sorted, csatSort.direction);
  }, [csatSort, csatTableData, csatCustomerNameById]);

  const handleCsatSort = (key) => {
    setCsatSort((prev) => getNextSortState(prev, key));
  };

  const getSortClass = (key) => getSortClassName(csatSort, key);
  const csatLastReceived = csatData?.lastReceived || '-';

  // ========== PROGRESS SORTING ==========
  const [progressSort, setProgressSort] = useState({ key: null, direction: 'asc' });

  const parseProgressDate = (value) => {
    if (!value) return 0;
    const parts = String(value).split('-');
    if (parts.length !== 3) return 0;
    const day = Number(parts[0]);
    const month = MONTH_INDEX_MAP[parts[1]] ?? 0;
    const year = Number(parts[2]);
    return new Date(year, month, day).getTime();
  };

  const progressSortedData = useMemo(() => {
    if (!progressSort.key) return progressData;
    const sorted = [...progressData].sort((firstRow, secondRow) => {
      const firstValue = firstRow[progressSort.key];
      const secondValue = secondRow[progressSort.key];
      if (progressSort.key === 'date') {
        return parseProgressDate(firstValue) - parseProgressDate(secondValue);
      }
      if (progressSort.key === 'remarks') {
        return String(firstValue || '').toLowerCase()
          .localeCompare(String(secondValue || '').toLowerCase());
      }
      if (typeof firstValue === 'number' && typeof secondValue === 'number') {
        return firstValue - secondValue;
      }
      return String(firstValue || '').localeCompare(String(secondValue || ''));
    });
    return applySortDirection(sorted, progressSort.direction);
  }, [progressSort, progressData]);

  const handleProgressSort = (key) => {
    setProgressSort((prev) => getNextSortState(prev, key));
  };

  const getProgressSortClass = (key) => getSortClassName(progressSort, key);

  // ========== AUDIT SORTING ==========
  const [auditSort, setAuditSort] = useState({ key: null, direction: 'asc' });

  const auditSortedData = useMemo(() => {
    if (!auditSort.key) return auditScoreData?.history || [];
    const sorted = [...(auditScoreData?.history || [])].sort((firstRow, secondRow) => {
      const firstValue = firstRow[auditSort.key];
      const secondValue = secondRow[auditSort.key];
      if (String(auditSort.key).toLowerCase()
        .includes('date')) {
        return parseProgressDate(firstValue) - parseProgressDate(secondValue);
      }
      const isFirstNum = typeof firstValue === 'number' || (firstValue !== null && firstValue !== '' && !Number.isNaN(Number(firstValue)));
      const isSecondNum = typeof secondValue === 'number' || (secondValue !== null && secondValue !== '' && !Number.isNaN(Number(secondValue)));
      if (isFirstNum && isSecondNum) {
        return Number(firstValue) - Number(secondValue);
      }
      return String(firstValue || '').localeCompare(String(secondValue || ''));
    });
    return applySortDirection(sorted, auditSort.direction);
  }, [auditSort, auditScoreData]);
  const isAuditEmpty = (auditSortedData || []).length === 0;

  const handleAuditSort = (key) => {
    setAuditSort((prev) => getNextSortState(prev, key));
  };

  const getAuditSortClass = (key) => getSortClassName(auditSort, key);

  // ========== MILESTONE ==========
  const milestoneItems = milestoneData?.milestones || [];

  // ========== RISK SUMMARY HELPERS ==========
  const riskRows = riskRegisterData?.risks || [];
  const issueRows = riskRegisterData?.issues || [];
  const infoSecRows = (
    riskRegisterData?.infoSecRisks
    || riskRegisterData?.infosecRisks
    || riskRegisterData?.infoSec
    || []
  );

  const [activeRiskTab, setActiveRiskTab] = useState(riskRegisterData?.tabs?.[0] || 'Risk');
  useEffect(() => {
    if (!riskVisibleTabs.length) return;
    if (!riskVisibleTabs.includes(activeRiskTab)) {
      setActiveRiskTab(riskVisibleTabs[0]);
    }
  }, [activeRiskTab, riskVisibleTabs]);

  const normalizeTabKey = (value) =>
    String(value || '')
      .toLowerCase()
      .replaceAll(/\s+/g, '');
  const activeRiskTabKey = normalizeTabKey(activeRiskTab);

  const toExposureScore = (row) => {
    const directScore = Number(row?.riskScoreValue);
    if (Number.isFinite(directScore)) return directScore;

    const riskScore = Number(row?.risk_score ?? row?.riskScore);
    if (Number.isFinite(riskScore)) return riskScore;

    const likelihood = Number(row?.likelihood_value ?? row?.likelihoodValue);
    const impact = Number(row?.impact_value ?? row?.impactValue);
    if (Number.isFinite(likelihood) && Number.isFinite(impact)) return likelihood * impact;

    return null;
  };
  const normalize = (value) => String(value || '')
    .trim()
    .toLowerCase();

  const getRiskStatusId = (row) => Number(
    row?.status,
    row?.risk_status_id
    ?? row?.riskStatusId
    ?? row?.infosec_status_id
    ?? row?.infosecStatusId
    ?? row?.status_id
    ?? row?.statusId,
  );

  const getRawStatusByType = (row, summaryType = 'risk') => {
    const statusByType = {
      issue: readFirstPresent(
        row?.issue_status_name,
        row?.issueStatusName,
        row?.status_name,
        row?.status,
      ),
      infosec: readFirstPresent(
        row?.status,
        row?.risk_status_name,
        row?.issue_status_name,
        row?.infosec_status_name,
        row?.infosecStatusName,
        row?.riskStatusName,
        row?.status_name,
      ),
      risk: readFirstPresent(
        row?.risk_status_name,
        row?.riskStatusName,
        row?.status_name,
        row?.status,
      ),
    };

    return normalize(statusByType[summaryType] ?? statusByType.risk);
  };

  const getBucketFromRules = (status, rules) => {
    const matchedRule = (Array.isArray(rules) ? rules : []).find(([, needles]) => (
      needles.some((needle) => status.includes(needle))
    ));
    return matchedRule ? matchedRule[0] : null;
  };

  const statusRulesByType = {
    issue: [
      ['closed', ['resolved', 'close']],
      ['escalated', ['awaiting']],
      ['open', ['hold', 'open']],
    ],
    infosec: [
      ['escalated', ['transfer', 'escalat']],
      ['closed', ['close']],
      ['open', ['open']],
    ],
    risk: [
      ['escalated', ['escalat']],
      ['closed', ['close']],
      ['open', ['open']],
    ],
  };

  const infosecStatusById = {
    1: 'open',
    2: 'closed',
    3: 'escalated',
    4: 'escalated',
  };

  const resolveStatusBucket = (row, summaryType = 'risk') => {
    const status = getRawStatusByType(row, summaryType);
    if (!status) return null;

    if (summaryType === 'infosec') {
      const statusId = getRiskStatusId(row);
      const bucketById = Number.isFinite(statusId) ? infosecStatusById[statusId] : null;
      if (bucketById) return bucketById;
    }

    const rules = statusRulesByType[summaryType] || statusRulesByType.risk;
    return getBucketFromRules(status, rules);
  };

  const applyPriorityBucket = (row, summaryType, current) => {
    const next = { ...current };
    const priorityText = normalize(row?.priority);
    if (summaryType === 'issue' && priorityText) {
      if (priorityText.includes('high') || priorityText.includes('critical')) next.high += 1;
      else if (priorityText.includes('medium')) next.medium += 1;
      else if (priorityText.includes('low')) next.low += 1;
      return next;
    }

    const score = toExposureScore(row);
    if (score === null) return next;

    if (summaryType === 'risk') {
      if (score > 8) next.high += 1;
      else if (score >= 4) next.medium += 1;
      else if (score >= 1) next.low += 1;
      return next;
    }

    // InfoSec risk uses RPN-style thresholds.
    if (score > 296) next.high += 1;
    else if (score >= 108) next.medium += 1;
    else next.low += 1;
    return next;
  };

  const isOverdueAndOpen = (row, summaryType, currentDay) => {
    const targetDate = row?.revised_target_date || row?.target_date;
    const rawStatus = getRawStatusByType(row, summaryType);
    const isClosedLike = rawStatus.includes('close') || rawStatus.includes('resolved');
    if (!targetDate || isClosedLike) return false;
    const dateObj = new Date(targetDate);
    dateObj.setHours(0, 0, 0, 0);
    return !Number.isNaN(dateObj.getTime()) && dateObj < currentDay;
  };

  const getSummary = (rows, summaryType = 'risk') => {
    const summary = {
      open: 0,
      closed: 0,
      escalated: 0,
      high: 0,
      medium: 0,
      low: 0,
      overdueOpen: 0,
    };
    const currentDay = new Date();
    currentDay.setHours(0, 0, 0, 0);
    const list = Array.isArray(rows) ? rows : [];

    list.forEach((row) => {
      const status = resolveStatusBucket(row, summaryType);
      if (status === 'open') summary.open += 1;
      if (status === 'closed') summary.closed += 1;
      if (status === 'escalated') summary.escalated += 1;

      const withPriority = applyPriorityBucket(row, summaryType, summary);
      summary.high = withPriority.high;
      summary.medium = withPriority.medium;
      summary.low = withPriority.low;

      if (isOverdueAndOpen(row, summaryType, currentDay)) summary.overdueOpen += 1;
    });

    return { total: list.length, ...summary };
  };

  const summaryType = getRiskSummaryType(activeRiskTabKey);
  const isInfoSecTab = summaryType === 'infosec';
  const activeRiskRows = getRiskRowsBySummaryType(summaryType, { riskRows, issueRows, infoSecRows });
  const visibleRiskRows = getVisibleRiskRowsBySummaryType(summaryType, { riskRows, infoSecRows });
  const activeSummary = getSummary(activeRiskRows, summaryType);
  const summaryConfig = getRiskSummaryConfig(summaryType, activeSummary);
  const riskRingData = buildRingSeries(
    summaryConfig.breakdown.map((item) => item.value),
    summaryConfig.breakdown.map(
      (item) => RISK_STATUS_RING_COLORS[item.dot] || RISK_STATUS_RING_COLORS.open,
    ),
  );
  const resourceRingData = buildRingSeries(
    [
      resourceAllocationData?.summary?.open || 0,
      resourceAllocationData?.summary?.closed || 0,
      resourceAllocationData?.summary?.transferred || 0,
    ],
    [RESOURCE_RING_COLORS.open, RESOURCE_RING_COLORS.closed, RESOURCE_RING_COLORS.escalated],
  );

  // ========== RESOURCE ALLOCATION SORTING ==========
  const [resourceSort, setResourceSort] = useState({ key: null, direction: 'asc' });

  const parseLongDate = (value) => {
    if (!value) return 0;
    const parts = String(value).split(', ');
    const datePart = parts.length > 1 ? parts[1] : parts[0];
    return parseProgressDate(datePart);
  };

  const parseResourceDateValue = (row, key) => {
    const value = row?.[key];
    if (!value) return 0;
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.valueOf() : 0;
  };

  const getDefaultResourceSortValue = (row) => (
    parseResourceDateValue(row, 'startDateValue')
    || parseResourceDateValue(row, 'endDateValue')
    || Number(row?.allocationSortValue || 0)
  );

  const resourceSortedData = useMemo(() => {
    const allocations = resourceAllocationData?.allocations || [];
    const sorted = [...allocations].sort((firstRow, secondRow) => {
      const statusPriorityDifference = getResourceStatusPriority(firstRow?.status)
        - getResourceStatusPriority(secondRow?.status);
      if (statusPriorityDifference !== 0) {
        return statusPriorityDifference;
      }

      if (!resourceSort.key) {
        return getDefaultResourceSortValue(secondRow) - getDefaultResourceSortValue(firstRow);
      }

      const firstValue = firstRow[resourceSort.key];
      const secondValue = secondRow[resourceSort.key];
      if (resourceSort.key === 'startDate' || resourceSort.key === 'endDate') {
        return parseLongDate(firstValue) - parseLongDate(secondValue);
      }
      if (typeof firstValue === 'number' && typeof secondValue === 'number') {
        return firstValue - secondValue;
      }
      return String(firstValue || '').localeCompare(String(secondValue || ''));
    });

    if (!resourceSort.key) return sorted;
    return applySortDirection(sorted, resourceSort.direction);
  }, [resourceSort, resourceAllocationData]);

  const handleResourceSort = (key) => {
    setResourceSort((prev) => getNextSortState(prev, key));
  };

  const getResourceSortClass = (key) => getSortClassName(resourceSort, key);

  // ========== RESOURCE COUNT SORTING ==========
  const [resourceCountSort, setResourceCountSort] = useState({ key: null, direction: 'asc' });
  const [expandedEffortRows, setExpandedEffortRows] = useState({});
  const [expandedRiskRows, setExpandedRiskRows] = useState({});

  // ========== INVOICE & COLLECTION EXPANSION STATE ==========
  const toggleRiskRow = (rowKey) => {
    setExpandedRiskRows((prev) => ({
      ...prev,
      [rowKey]: !prev[rowKey],
    }));
  };
  const toggleEffortRow = (rowKey) => {
    setExpandedEffortRows((prev) => ({
      ...prev,
      [rowKey]: !prev[rowKey],
    }));
  };

  // eslint-disable-next-line no-unused-vars
  const renderRiskIdentityCell = (row, rowKey, isExpanded) => (
    <td className="td-cell">
      <div className="risk-id-cell">
        <button
          type="button"
          className={`expand-row-btn ${isExpanded ? 'expanded' : ''}`}
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
          aria-expanded={isExpanded}
          onClick={() => toggleRiskRow(rowKey)}
        >
          ›
        </button>
        <div className="risk-id-text">
          <div className="risk-id">{row.id || ''}</div>
          <span
            className={`status-badge ${(row.status || '')
              .toLowerCase()
              .replaceAll(' ', '-')}`}
          >
            {row.status || ''}
          </span>
          {row.description && isExpanded && (
            <div className="risk-desc">{row.description}</div>
          )}
        </div>
      </div>
    </td>
  );

  const resourceCountSortedData = useMemo(() => {
    if (!resourceCountSort.key) return resourceCountData || [];
    const sorted = [...(resourceCountData || [])].sort((firstRow, secondRow) => {
      const firstValue = firstRow[resourceCountSort.key];
      const secondValue = secondRow[resourceCountSort.key];
      if (typeof firstValue === 'number' && typeof secondValue === 'number') {
        return firstValue - secondValue;
      }
      return String(firstValue || '').localeCompare(String(secondValue || ''));
    });
    return applySortDirection(sorted, resourceCountSort.direction);
  }, [resourceCountSort, resourceCountData]);

  const handleResourceCountSort = (key) => {
    setResourceCountSort((prev) => getNextSortState(prev, key));
  };

  const getResourceCountSortClass = (key) => getSortClassName(resourceCountSort, key);

  const resourceCountTotals = useMemo(() => ({
    total: (resourceCountData || []).reduce((sum, row) => sum + (row.total || 0), 0),
    current: (resourceCountData || []).reduce((sum, row) => sum + (row.current || 0), 0),
    allocation: (resourceCountData || []).reduce((sum, row) => sum + (row.allocation || 0), 0),
  }), [resourceCountData]);

  const isCsatEmpty = csatSortedData.length === 0;

  const sanitizeExportText = (value) => String(value || '')
    .replaceAll(/\s+/g, ' ')
    .trim();

  const getExportFileName = (title) => sanitizeExportText(title)
    .replaceAll(/[^\w\s-]/g, '')
    .replaceAll(/\s+/g, '_')
    || 'section_report';

  const getExportTitleWithTimestamp = (title) => {
    const now = new Date();
    const parts = [
      now.getFullYear(),
      String(now.getMonth() + 1).padStart(2, '0'),
      String(now.getDate()).padStart(2, '0'),
      String(now.getHours()).padStart(2, '0'),
      String(now.getMinutes()).padStart(2, '0'),
      String(now.getSeconds()).padStart(2, '0'),
    ];
    return `${sanitizeExportText(title)} - ${parts.join('-')}`;
  };

  const FULL_DAY_NAMES = {
    Sun: 'Sunday',
    Mon: 'Monday',
    Tue: 'Tuesday',
    Wed: 'Wednesday',
    Thu: 'Thursday',
    Fri: 'Friday',
    Sat: 'Saturday',
  };

  const printRowsAsPdf = (title, rows) => {
    const keys = rows.length ? Object.keys(rows[0]) : [];
    if (!keys.length) return;

    const buildTable = (doc, tableKeys, tableRows) => {
      const table = doc.createElement('table');
      const thead = doc.createElement('thead');
      const headerRow = doc.createElement('tr');

      for (const key of tableKeys) {
        const th = doc.createElement('th');
        th.textContent = sanitizeExportText(key);
        headerRow.appendChild(th);
      }
      thead.appendChild(headerRow);

      const tbody = doc.createElement('tbody');

      for (const row of tableRows) {
        const tr = doc.createElement('tr');

        for (const key of tableKeys) {
          const td = doc.createElement('td');
          td.textContent = sanitizeExportText(row[key]);
          tr.appendChild(td);
        }

        tbody.appendChild(tr);
      }

      table.appendChild(thead);
      table.appendChild(tbody);
      return table;
    };

    const printableTitle = sanitizeExportText(title) || 'Section';
    const printWindow = window.open('', '_blank', 'noopener,noreferrer');
    if (!printWindow) return;

    const printDocument = printWindow.document;
    printDocument.title = printableTitle;

    const style = printDocument.createElement('style');
    style.textContent = [
      'body { font-family: Arial, sans-serif; padding: 20px; color: #1d1b20; }',
      'h2 { margin: 0 0 12px; }',
      'table { width: 100%; border-collapse: collapse; margin-top: 12px; }',
      'th, td { border: 1px solid #d9dbe3; padding: 8px; text-align: left; font-size: 12px; vertical-align: top; }',
      'th { background: #f5f6fb; font-weight: 600; }',
    ].join('');
    printDocument.head.appendChild(style);

    const heading = printDocument.createElement('h2');
    heading.textContent = printableTitle;
    printDocument.body.appendChild(heading);
    printDocument.body.appendChild(buildTable(printDocument, keys, rows));

    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const runSectionDownload = async (format, title, rows, options = {}) => {
    if (!rows.length) {
      snackbarOpen('No data to download', 'warning');
      return;
    }

    if (format === 'excel') {
      await exportToExcel({
        data: rows,
        workBookTitle: getExportFileName(title),
        workSheetTitle: title,
        columnsConfig: options.columnsConfig || null,
        hasTotalRow: options.hasTotalRow ?? false,
        totalCellAlignment: options.totalCellAlignment || 'right',
        borderAllCells: options.borderAllCells ?? false,
        outerBorder: options.outerBorder ?? false,
      });
      return;
    }

    printRowsAsPdf(title, rows);
  };

  const getCsatExportRows = () => csatSortedData.map((row, index) => ({
    'SI': index + 1,
    'Project Id': projectId || '-',
    'Project Name': projectName || '-',
    'Project Vertical': projectVertical || '-',
    'Project Type': projectData?.projectHeader?.basicDetails?.find((item) => item.label === 'Type')?.value || (projectTypeVariant === 'fp' ? 'Fixed Price' : projectTypeVariant === 'tm' ? 'Time & Material' : '-'),
    'Project Status': projectStatus || '-',
    'Project Manager': projectData?.peopleData?.srManagerDisplay || '-',
    'Baseline Start Date': projectData?.projectHeader?.timeline?.rlsd || '-',
    'Baseline End Date': projectData?.projectHeader?.timeline?.rled || '-',
    'Actual Start Date': projectData?.projectHeader?.timeline?.acsd || '-',
    'Replanned End Date': projectData?.projectHeader?.timeline?.rped || '-',
    'CSAT Received': row.receivedDate || '-',
    'Quality of service': row.serviceQuality ?? '-',
    'Return-On-Investment': row.roi ?? '-',
    'Project management and communication': row.management ?? '-',
    'Overall experience': row.overall ?? '-',
    'Customer Name': resolveCsatCustomerName(row) || '-',
    'Other Feedback': row.feedback || '',
  }));

  const getProgressExportRows = () => progressSortedData.map((row) => ({
    date: row.date || '',
    day: FULL_DAY_NAMES[row.day] || row.day || '',
    progressPercent: Number(row.progress || 0).toFixed(2),
    comments: row.remarks || '',
  }));

  const getEffortsExportRows = () => {
    const groupedRows = (effortsData?.groups || []).flatMap((group) => [
      {
        crNumber: group.crNumber || '-',
        crName: group.crName || '-',
        effortInvestment: group.effortInvestment || '-',
        crStatus: group.crStatus || '-',
        customerApprovalStatus: group.customerApprovalStatus || '-',
        resourceCategory: '-',
        resourceSkills: '-',
        effortHrs: Number(group.totalHours || 0).toFixed(2),
      },
      ...((group.children || []).map((row) => ({
        crNumber: '',
        crName: '',
        effortInvestment: '',
        crStatus: '',
        customerApprovalStatus: '',
        resourceCategory: row.resource_category || '',
        resourceSkills: row.resource_type || '',
        effortHrs: Number(row.efforts_hours || 0).toFixed(2),
      }))),
    ]);

    return [
      ...groupedRows,
      {
        crNumber: 'Total',
        crName: '',
        effortInvestment: '',
        crStatus: '',
        customerApprovalStatus: '',
        resourceCategory: '',
        resourceSkills: '',
        effortHrs: Number(
          (effortsData?.rows || []).reduce((sum, row) => sum + Number(row?.efforts_hours || 0), 0),
        ).toFixed(2),
      },
    ];
  };

  const formatAuditExportScore = (score) => {
    if (score === null || score === undefined || score === '') return '-';
    const trimmed = String(score).trim();
    if (trimmed.toUpperCase() === 'NA') return 'NA';
    if (trimmed === '-') return '-';
    const num = Number(score);
    return Number.isFinite(num) ? num.toFixed(2) : String(score);
  };

  const getAuditExportRows = () => auditSortedData.map((row) => ({
    processComplianceDate: row.processDate || '',
    processCompliance: formatAuditExportScore(row.processScore),
    qualityAuditDate: row.qualityDate || '',
    quality: formatAuditExportScore(row.qualityScore),
    technologyAuditDate: row.technologyDate || '',
    technology: formatAuditExportScore(row.technologyScore),
  }));

  const getIssueExportRows = () => issueRows.map((row, index) => ({
    serialNo: index + 1,
    issueId: toExportValue(row.id),
    issueDescription: toExportValue(row.description),
    status: toExportValue(row.status),
    issueCategory: toExportValue(row.category),
    priority: toExportValue(row.priority),
    assignedTo: toExportValue(row.assignedTo),
    raisedOn: toExportValue(row.raisedDate),
    targetDate: toExportValue(row.targetDate),
    revisedDate: toExportValue(row.revisedDate),
    statusUpdates: toExportValue(row.statusUpdates),
    lastUpdatedDate: toExportValue(row.lastUpdated),
  }));

  const getRiskExportRows = (rows) => rows.map((row, index) => ({
    serialNo: index + 1,
    riskId: toExportValue(row.id),
    riskDescription: toExportValue(row.description),
    status: toExportValue(row.status),
    riskCategory: toExportValue(row.category),
    riskScore: toExportValue(row.riskScore),
    likelihood: toExportValue(row.likelihood),
    impact: toExportValue(row.impact),
    owner: toExportValue(row.owner),
    raisedOn: toExportValue(row.raisedDate),
    targetDate: toExportValue(row.targetDate),
    revisedDate: toExportValue(row.revisedDate),
    mitigation: toExportValue(row.mitigation),
    measures: toExportValue(row.measures),
    lastUpdatedDate: toExportValue(row.lastUpdated),
  }));

  const getInfoSecExportRows = (rows) => rows.map((row, index) => ({
    serialNo: index + 1,
    infoSecIssue: toExportValue(row.id),
    infoSecDescription: toExportValue(row.description),
    assetCategory: toExportValue(readFirstPresent(row.assetCategory, row.category)),
    riskScore: toExportValue(row.riskScore),
    likelihood: toExportValue(row.likelihood),
    impact: toExportValue(row.impact),
    owner: toExportValue(row.owner),
    raisedOn: toExportValue(row.raisedDate),
    targetDate: toExportValue(row.targetDate),
    revisedDate: toExportValue(row.revisedDate),
    riskMitigationStrategy: toExportValue(row.mitigation),
    measuresToAddressRisk: toExportValue(row.measures),
    lastUpdatedDate: toExportValue(row.lastUpdated),
  }));

  const getMilestoneExportRows = () => milestoneItems.map((item) => ({
    milestoneId: readFirstPresent(item.numericId, item.id),
    milestoneName: toExportValue(item.name),
    category: toExportValue(item.category),
    milestoneStatus: readFirstPresent(item.milestoneStatus, item.status),
    invoiceStatus: toExportValue(item.invoiceStatus),
    amountINR: toExportValue(item.amount),
    dueDate: toExportValue(item.dueDate),
    replannedDueDate: toExportValue(item.replannedDate),
    poNumber: toExportValue(item.poNumber),
    poLink: toExportValue(item.poLink),
  }));

  const getResourceCountExportRows = () => [
    ...resourceCountSortedData.map((row) => ({
      role: row.role || '',
      totalResourceHeadCount: row.total || 0,
      currentResourceHeadCount: row.current || 0,
      currentResourceAllocation: row.allocation || 0,
    })),
    {
      role: 'Total',
      totalResourceHeadCount: resourceCountTotals.total,
      currentResourceHeadCount: resourceCountTotals.current,
      currentResourceAllocation: resourceCountTotals.allocation,
    },
  ];

  const getResourceDetailsExportRows = () => resourceSortedData.map((row) => ({
    employeeName: row.name || '',
    employeeId: row.employeeId || '',
    role: row.role || '',
    status: row.status || '',
    skills: row.skill || '',
    allocationPercent: row.allocation || 0,
    startDate: normalizeUiDateToDdMmmYyyy(row.startDate),
    endDate: normalizeUiDateToDdMmmYyyy(row.endDate),
    allocatedDays: Number(row.allocatedDays || 0).toFixed(2),
    allocatedHours: Number(row.allocatedHours || 0).toFixed(2),
  }));

  const getProjectUnitsExportRows = () => {
    const list = projectUnitsData?.units || projectUnitsData?.rows || [];

    // 1. Map the standard rows
    const rows = list.map((row, index) => ({
      serialNo: index + 1,
      resourceCategory: row.resourceCategory || '',
      skills: row.skills || '',
      quantity: Number(row.quantity || 0),
      unitPrice: Number(row.unitPrice || 0),
      startDate: normalizeUiDateToDdMmmYyyy(row.startDate) || '',
      endDate: normalizeUiDateToDdMmmYyyy(row.endDate) || '',
      remarks: row.remarks || '',
    }));

    // 2. Calculate totals
    if (rows.length > 0) {
      const totalQuantity = rows.reduce((sum, row) => sum + row.quantity, 0);
      const totalUnitPrice = rows.reduce((sum, row) => sum + row.unitPrice, 0);

      // 3. Append the Total row at the bottom
      rows.push({
        serialNo: '',
        resourceCategory: 'Total', // Leave blank so it looks clean
        skills: '',
        quantity: totalQuantity,
        unitPrice: totalUnitPrice,
        startDate: '',
        endDate: '',
        remarks: '',
      });
    }

    return rows;
  };

  // Helper to compute CTC and margin flag for T&M allocations (export version)

  const createDownloadHandler = (rowsGetter, titleTransformer = (title) => title, options = {}) => async (
    { format, title },
  ) => {
    await runSectionDownload(format, titleTransformer(title), rowsGetter(), options);
  };

  const handleStaffingRequestClick = useCallback(() => {
    if (!canCreateResourceAllocationDetails) return;
    navigate('/delivery/staffing-request', {
      state: { projectId },
    });
  }, [navigate, projectId]);

  const csatColumnsConfig = [
    { header: 'SI', key: 'SI', width: 10 },
    { header: 'Project Id', key: 'Project Id', width: 15 },
    { header: 'Project Name', key: 'Project Name', width: 30 },
    { header: 'Project Vertical', key: 'Project Vertical', width: 20 },
    { header: 'Project Type', key: 'Project Type', width: 15 },
    { header: 'Project Status', key: 'Project Status', width: 15 },
    { header: 'Project Manager', key: 'Project Manager', width: 25 },
    { header: 'Baseline Start Date', key: 'Baseline Start Date', width: 20 },
    { header: 'Baseline End Date', key: 'Baseline End Date', width: 20 },
    { header: 'Actual Start Date', key: 'Actual Start Date', width: 20 },
    { header: 'Replanned End Date', key: 'Replanned End Date', width: 20 },
    { header: 'CSAT Received', key: 'CSAT Received', width: 20 },
    { header: 'Quality of service', key: 'Quality of service', width: 20 },
    { header: 'Return-On-Investment', key: 'Return-On-Investment', width: 20 },
    { header: 'Project management and communication', key: 'Project management and communication', width: 25 },
    { header: 'Overall experience', key: 'Overall experience', width: 20 },
    { header: 'Customer Name', key: 'Customer Name', width: 25 },
    { header: 'Other Feedback', key: 'Other Feedback', width: 40 },
  ];

  const handleCsatDownload = async ({ format, title }) => {
    const rows = getCsatExportRows();
    if (!rows.length) {
      snackbarOpen('No data to download', 'warning');
      return;
    }
    const exportTitle = title || 'CSAT';
    const fileName = getExportFileName(exportTitle);

    if (format === 'excel') {
      // Send the data directly to the smart utility
      // It will automatically handle the right-alignments for numbers.
      await exportToExcel({
        data: rows,
        workBookTitle: fileName,
        workSheetTitle: exportTitle,
        columnsConfig: csatColumnsConfig,
      });
      return;
    }

    printRowsAsPdf(exportTitle, rows);
  };
  const csatMoreMenuItems = useMemo(() => ([
    ...(canCreateCsat ? [
      { id: 'add-csat', label: 'Add CSAT' },
      { id: 'add-customer-details', label: 'Add customer details', disabled: !canCreateCsatCustomerDetails },
    ] : []),
    { id: 'csat-mis', label: 'CSAT MIS', disabled: !canViewMisCsat },
    { id: 'customer-details-mis', label: 'Customer details MIS', disabled: !canViewCsatCustomerDetails },
  ]), [canCreateCsat, canCreateCsatCustomerDetails, canViewCsatCustomerDetails, canViewMisCsat]);
  const progressMoreMenuItems = useMemo(() => (
    canCreateProjectProgress ? [{ id: 'add-progress', label: 'Add progress %' }] : []
  ), [canCreateProjectProgress]);
  const budgetChangeRequestMenuItem = useMemo(() => ({
    id: 'budget-change-request',
    label: 'Budget Change Request',
  }), []);
  const changeRequestMenuItem = useMemo(() => ({
    id: 'change-request',
    label: 'Change Request',
  }), []);
  const resourceLoadingPlanMenuItem = useMemo(() => ({
    id: 'resource-loading-plan',
    label: 'Resource Loading Plan',
  }), []);
  const effortsMoreMenuItems = useMemo(() => ([
    ...(canCreateEffortEstimation ? [{ id: 'add-update-effort', label: 'Add effort' }] : []),
    ...(projectTypeVariant === 'fp' ? [
      budgetChangeRequestMenuItem,
      ...(canViewChangeRequest ? [changeRequestMenuItem] : []),
      ...(canViewResourceLoadingPlan ? [resourceLoadingPlanMenuItem] : []),
    ] : []),
  ]), [
    budgetChangeRequestMenuItem,
    canCreateEffortEstimation,
    canViewChangeRequest,
    canViewResourceLoadingPlan,
    changeRequestMenuItem,
    projectTypeVariant,
    resourceLoadingPlanMenuItem,
  ]);
  const projectUnitsMoreMenuItems = useMemo(() => (
    projectTypeVariant === 'tm'
      ? [
        budgetChangeRequestMenuItem,
        ...(canViewResourceLoadingPlan ? [resourceLoadingPlanMenuItem] : []),
        ...(canCreateProjectUnit ? [{ id: 'add-project-unit', label: 'Add New Unit' }] : []),
      ]
      : []
  ), [
    budgetChangeRequestMenuItem,
    canCreateProjectUnit,
    canViewResourceLoadingPlan,
    projectTypeVariant,
    resourceLoadingPlanMenuItem,
  ]);
  const auditMoreMenuItems = useMemo(() => ([
    ...(canCreateAuditScore ? [{ id: 'add-audit-score', label: 'Add audit score' }] : []),
  ]), [canCreateAuditScore]);
  const riskMoreMenuItems = useMemo(() => ([
    { id: 'risk-report-mis', label: 'Risk report MIS', disabled: !canViewMisRiskRegister },
    { id: 'issue-report-mis', label: 'Issue report MIS', disabled: !canViewMisRiskRegister },
    { id: 'infosec-report-mis', label: 'InfoSec report MIS', disabled: !canViewMisRiskRegister },
    ...(canCreateRisk ? [{ id: 'add-risk', label: 'Add Risk' }] : []),
    ...(canCreateIssue ? [{ id: 'add-issues', label: 'Add Issues' }] : []),
    ...(canCreateInfoSecRisk ? [{ id: 'add-infosec-risk', label: 'Add Infosec risk' }] : []),
  ]), [canViewMisRiskRegister, canCreateInfoSecRisk, canCreateIssue, canCreateRisk]);
  const milestoneMoreMenuItems = useMemo(() => ([
    {
      id: 'navigate-milestone',
      label: 'Navigate milestone',
      disabled: !projectId,
    },
  ]), [projectId]);
  const resourceDetailsMoreMenuItems = useMemo(() => ([
    ...(canCreateResourceAllocationDetails ? [{ id: 'staffing-request', label: 'Staffing request', disabled: false, onClick: handleStaffingRequestClick }] : []),
    ...(canUpdateResourceAllocationDetails ? [{ id: 'change-end-date', label: 'Change end date', disabled: true }] : []),
  ]), [canCreateResourceAllocationDetails, canUpdateResourceAllocationDetails]);

  const tbhResourcesMoreMenuItems = useMemo(() => ([
    ...(canCreateResourceAllocationDetails ? [{
      id: 'add-tbh-resources',
      label: 'Add TBH resources',
      disabled: false,
      onClick: () => setIsAddTbhResourceOpen(true),
    }] : []),
  ]), [canCreateResourceAllocationDetails]);

  const handleResourceEndDateChange = useCallback((resourceRow) => {
    setEditResourceData({
      ...resourceRow,
      startDate: normalizeDateForApi(dayjs().startOf('day')),
      endDate: normalizeDateForApi(resourceRow?.endDateValue || resourceRow?.endDate),
      currentAllocation: Number(resourceRow?.allocation) || 0,
      comments: '',
      isRollOff: false,
    });
    setIsUpdateDialogOpen(true);
  }, []);

  const handleProgressDownload = async ({ format }) => {
    const rows = getProgressExportRows();

    if (!rows.length) {
      snackbarOpen('No data to download', 'warning');
      return;
    }

    const exportTitle = getExportTitleWithTimestamp('Progress');

    if (format === 'excel') {
      await exportToExcel({
        data: rows,
        workBookTitle: getExportFileName(exportTitle),
        workSheetTitle: exportTitle,
        borderAllCells: true,
        columnsConfig: [
          { header: 'Date', key: 'date', width: 18 },
          { header: 'Day', key: 'day', width: 18 },
          { header: 'Progress %', key: 'progressPercent', width: 16 },
          { header: 'Comments', key: 'comments', width: 42 },
        ],
        prependRows: [
          {
            values: [`Project Name: ${sanitizeExportText(projectName) || '-'}`],
            mergeAcross: 4,
            font: { bold: true, size: 12 },
            alignment: { horizontal: 'left', vertical: 'middle' },
          },
          {
            values: [''],
            mergeAcross: 4,
          },
        ],
      });
      return;
    }

    printRowsAsPdf(exportTitle, rows);
  };
  const handleEffortsDownload = createDownloadHandler(getEffortsExportRows);
  const handleAuditDownload = createDownloadHandler(getAuditExportRows);
  const handleRiskDownload = async ({ format, title }) => {
    const riskDownloadByType = {
      issue: {
        rows: getIssueExportRows(),
        suffix: 'Issue',
        columnsConfig: [
          { header: 'Si. No', key: 'serialNo', width: 10 },
          { header: 'Issues', key: 'issueId', width: 18 },
          { header: 'Issue description', key: 'issueDescription', width: 36 },
          { header: 'Status', key: 'status', width: 18 },
          { header: 'Issue category', key: 'issueCategory', width: 24 },
          { header: 'Priority', key: 'priority', width: 16 },
          { header: 'Assigned to', key: 'assignedTo', width: 22 },
          { header: 'Raised on', key: 'raisedOn', width: 16 },
          { header: 'Target date', key: 'targetDate', width: 16 },
          { header: 'Revised Target Date', key: 'revisedDate', width: 20 },
          { header: 'Status Updates/Actions taken', key: 'statusUpdates', width: 32 },
          { header: 'Last Updated Date', key: 'lastUpdatedDate', width: 18 },
        ],
      },
      infosec: {
        rows: getInfoSecExportRows(infoSecRows),
        suffix: 'InfoSec',
        columnsConfig: [
          { header: 'Si. No', key: 'serialNo', width: 10 },
          { header: 'InfoSec Issues', key: 'infoSecIssue', width: 18 },
          { header: 'InfoSec description', key: 'infoSecDescription', width: 36 },
          { header: 'Asset category', key: 'assetCategory', width: 24 },
          { header: 'Risk score', key: 'riskScore', width: 14 },
          { header: 'Likelihood', key: 'likelihood', width: 18 },
          { header: 'Impact', key: 'impact', width: 18 },
          { header: 'Owner', key: 'owner', width: 22 },
          { header: 'Raised on', key: 'raisedOn', width: 16 },
          { header: 'Target date', key: 'targetDate', width: 16 },
          { header: 'Revised Target Date', key: 'revisedDate', width: 20 },
          { header: 'Risk mitigation strategy', key: 'riskMitigationStrategy', width: 28 },
          { header: 'Measures to address risk', key: 'measuresToAddressRisk', width: 32 },
          { header: 'Last Updated Date', key: 'lastUpdatedDate', width: 18 },
        ],
      },
      risk: {
        rows: getRiskExportRows(riskRows),
        suffix: 'Risk',
        columnsConfig: [
          { header: 'Si. No', key: 'serialNo', width: 10 },
          { header: 'Risk', key: 'riskId', width: 18 },
          { header: 'Risk description', key: 'riskDescription', width: 36 },
          { header: 'Risk category', key: 'riskCategory', width: 24 },
          { header: 'Risk score', key: 'riskScore', width: 14 },
          { header: 'Likelihood', key: 'likelihood', width: 18 },
          { header: 'Impact', key: 'impact', width: 18 },
          { header: 'Owner', key: 'owner', width: 22 },
          { header: 'Raised on', key: 'raisedOn', width: 16 },
          { header: 'Target date', key: 'targetDate', width: 16 },
          { header: 'Revised Target Date', key: 'revisedDate', width: 20 },
          { header: 'Risk mitigation strategy', key: 'mitigation', width: 28 },
          { header: 'Measures to address risk', key: 'measures', width: 32 },
          { header: 'Last Updated Date', key: 'lastUpdatedDate', width: 18 },
        ],
      },
    };
    const selected = riskDownloadByType[summaryType] || riskDownloadByType.risk;
    await runSectionDownload(
      format,
      `${title} - ${selected.suffix}`,
      selected.rows,
      { columnsConfig: selected.columnsConfig },
    );
  };
  const handleMilestoneDownload = createDownloadHandler(getMilestoneExportRows);
  const handleResourceCountDownload = createDownloadHandler(getResourceCountExportRows);
  const handleResourceDetailsDownload = createDownloadHandler(getResourceDetailsExportRows);

  const projectUnitsColumnsConfig = [
    { header: 'Sl. No', key: 'serialNo', width: 10 },
    { header: 'Resource Category', key: 'resourceCategory', width: 25 },
    { header: 'Resource Skills', key: 'skills', width: 25 },
    { header: 'Qty', key: 'quantity', width: 15 },
    { header: 'Unit Cost/M', key: 'unitPrice', width: 20 },
    { header: 'Start Date', key: 'startDate', width: 18 },
    { header: 'End Date', key: 'endDate', width: 18 },
    { header: 'Remarks', key: 'remarks', width: 35 },
  ];

  const handleProjectUnitsDownload = createDownloadHandler(
    getProjectUnitsExportRows,
    (title) => `${title}_report`,
    { columnsConfig: projectUnitsColumnsConfig, hasTotalRow: true, totalCellAlignment: 'right' },
  );

  const formatDateToDMY = (dateVal) => {
    if (!dateVal || dateVal === '-') return '-';
    const parsedDate = dayjs(dateVal);
    if (!parsedDate.isValid()) return String(dateVal);
    return parsedDate.format('D-MMM-YYYY');
  };

  const getTbhExportRows = () => tbhResources.map((row, index) => ({
    serialNo: index + 1,
    tbhId: row.tbhId || '-',
    vertical: row.vertical || '-',
    regionName: row.regionName || '-',
    countryName: row.countryName || '-',
    roleName: row.roleName || '-',
    experience: row.experience || '-',
    location: row.location || '-',
    department: row.department || '-',
    allocationPercent: row.allocationPct ? `${Number(row.allocationPct).toFixed(2)}%` : '0.00%',
    startDate: formatDateToDMY(row.startDate),
    endDate: formatDateToDMY(row.endDate),
    allocatedDays: Number(row.allocatedDays || 0).toFixed(2),
    allocatedHours: Number(row.allocatedHours || 0).toFixed(2),
    remark: row.remark || '-',
    scrumTeamName: row.scrumTeamName || '-',
    isActive: row.isActive === 1 || row.isActive === true ? 'Yes' : 'No',
  }));

  const handleTbhDownload = async ({ format, title }) => {
    const rows = getTbhExportRows();
    if (!rows.length) {
      snackbarOpen('No data to download', 'warning');
      return;
    }
    const exportTitle = title || 'TBH Resources';
    const fileName = 'TBH_Resources_report';

    if (format === 'excel') {
      const ExcelJS = (await import('exceljs')).default;
      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'Darpan 2';
      workbook.created = new Date();
      const worksheet = workbook.addWorksheet(exportTitle);

      const tbhColumnsConfig = [
        { header: 'Sl', key: 'serialNo', width: 10 },
        { header: 'TBH Id', key: 'tbhId', width: 15 },
        { header: 'Employee Vertical', key: 'vertical', width: 18 },
        { header: 'Employee Region', key: 'regionName', width: 18 },
        { header: 'Employee Country', key: 'countryName', width: 18 },
        { header: 'Employee Role', key: 'roleName', width: 24 },
        { header: 'Employee Experience', key: 'experience', width: 26 },
        { header: 'Employee Location', key: 'location', width: 18 },
        { header: 'Employee Department', key: 'department', width: 22 },
        { header: 'Allocation%', key: 'allocationPercent', width: 16 },
        { header: 'Start Date', key: 'startDate', width: 18 },
        { header: 'End Date', key: 'endDate', width: 18 },
        { header: 'Allocated Days', key: 'allocatedDays', width: 16 },
        { header: 'Allocated Hours', key: 'allocatedHours', width: 16 },
        { header: 'Remarks', key: 'remark', width: 30 },
        { header: 'Scrum', key: 'scrumTeamName', width: 20 },
        { header: 'Is Active', key: 'isActive', width: 15 },
      ];

      worksheet.columns = tbhColumnsConfig;

      // Header row formatting
      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true };
      headerRow.height = 40;
      headerRow.eachCell((cell, colNumber) => {
        const columnKey = tbhColumnsConfig[colNumber - 1].key;
        cell.alignment = {
          horizontal: columnKey === 'allocationPercent' ? 'right' : 'left',
          wrapText: true,
          vertical: 'middle',
        };
        cell.border = {
          top: { style: 'thin', color: { argb: 'FFD9DBE3' } },
          left: { style: 'thin', color: { argb: 'FFD9DBE3' } },
          bottom: { style: 'thin', color: { argb: 'FFD9DBE3' } },
          right: { style: 'thin', color: { argb: 'FFD9DBE3' } },
        };
      });

      // Data rows formatting
      rows.forEach((row) => {
        const newRow = worksheet.addRow(row);
        newRow.eachCell((cell, colNumber) => {
          const columnKey = tbhColumnsConfig[colNumber - 1].key;
          cell.alignment = {
            horizontal: columnKey === 'allocationPercent' ? 'right' : 'left',
            vertical: 'middle',
          };
          cell.border = {
            top: { style: 'thin', color: { argb: 'FFD9DBE3' } },
            left: { style: 'thin', color: { argb: 'FFD9DBE3' } },
            bottom: { style: 'thin', color: { argb: 'FFD9DBE3' } },
            right: { style: 'thin', color: { argb: 'FFD9DBE3' } },
          };
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${fileName}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    printRowsAsPdf(exportTitle, rows);
  };

  // ========== MODAL HANDLERS ==========
  // Generic modal handlers
  const openModal = (modalType, data = null) => {
    setModalState((prev) => ({
      ...prev,
      [modalType]: { isOpen: true, data },
    }));
  };

  const closeModal = (modalType) => {
    setModalState((prev) => ({
      ...prev,
      [modalType]: { isOpen: false, data: null },
    }));

    if (['riskEdit', 'issueEdit', 'infoSecEdit'].includes(modalType)) {
      const newParams = new URLSearchParams(location.search);
      if (newParams.has('editId') || newParams.has('editType')) {
        newParams.delete('editId');
        newParams.delete('editType');
        const newSearch = newParams.toString();
        navigate(
          {
            pathname: location.pathname,
            search: newSearch ? `?${newSearch}` : '',
          },
          { replace: true },
        );
      }
    }
  };
  const hasValidRowIdentifier = (row) => Boolean(row?.id ?? row?.row_id);
  const confirmEntityDelete = async ({
    modalKey,
    entityType,
    successMessage,
    failureMessage,
  }) => {
    const targetRow = modalState[modalKey].data;
    if (isActionInProgress) return;
    if (!projectSeqId || !employeeId || !hasValidRowIdentifier(targetRow)) {
      snackbarOpen('Missing required fields. Please provide entity_type, row_id, and employeeId.', 'error');
      return;
    }
    setIsActionInProgress(true);
    try {
      const response = await UseAxios(deleteProjectDetails(projectSeqId, getDeletePayload(targetRow, entityType)));
      assertApiSuccess(response, failureMessage);
      snackbarOpen(successMessage, 'success');
      closeModal(modalKey);
      if (typeof onRefreshData === 'function') onRefreshData();
    } catch (err) {
      snackbarOpen(getErrorMessage(err, failureMessage), 'error');
    } finally {
      setIsActionInProgress(false);
    }
  };

  const handleMilestoneEdit = (milestone) => {
    const resolvedProjectId = projectId ?? milestone?.project_id ?? milestone?.projectId ?? null;
    const resolvedMilestoneId = milestone?.milestone_id ?? milestone?.milestoneId ?? null;

    if (!resolvedProjectId || !resolvedMilestoneId) {
      snackbarOpen('Unable to open milestone edit. Missing project or milestone id.', 'error');
      return;
    }

    const params = new URLSearchParams({
      projectId: String(resolvedProjectId),
      editType: 'milestone',
      milestoneId: String(resolvedMilestoneId),
    });

    if (projectName) {
      params.set('projectName', String(projectName));
    }

    window.open(`/finance/milestone-details?${params.toString()}`, '_blank', 'noopener,noreferrer');
  };

  const handleMilestoneMore = useCallback((menuItem) => {
    if (menuItem?.id !== 'navigate-milestone') return;

    if (!projectId) {
      snackbarOpen('Unable to open Finance Milestone. Missing project id.', 'error');
      return;
    }

    const params = new URLSearchParams({
      projectId: String(projectId),
    });

    if (projectName) {
      params.set('projectName', String(projectName));
    }

    window.open(`/finance/milestone-details?${params.toString()}`, '_blank', 'noopener,noreferrer');
  }, [projectId, projectName, snackbarOpen]);

  // ========== INVOICE HANDLERS ==========
  const handleInvoiceAdd = (milestonePayload) => openModal('invoiceAdd', milestonePayload);
  const handleInvoiceEdit = (invoice) => openModal('invoiceEdit', invoice);

  // ========== COLLECTION HANDLERS ==========
  const handleCollectionAdd = (invoiceData) => openModal('collectionAdd', invoiceData);
  const handleCollectionEdit = (collection) => openModal('collectionEdit', collection);

  // CSAT handlers
  const handleCSATEdit = (row) => openModal('csatEdit', row);
  const handleCSATHistoryRedirect = useCallback(() => {
    navigate(`/projects/csat-history?projectId=${projectId}`);
  }, [navigate, projectId]);
  const handleCSATMore = async (menuItem) => {
    if (menuItem?.id === 'csat-mis') {
      handleCSATHistoryRedirect();
      return;
    }
    if (menuItem?.id === 'customer-details-mis') {
      navigate(`/projects/csat-customer-details?projectId=${projectId}`);
      return;
    }
    if (menuItem?.id === 'add-customer-details') {
      navigate(`/projects/csat-customer-details?projectId=${projectId}`, { state: { openAdd: true } });
      return;
    }
    if (!canCreateCsat) return;
    if (menuItem?.id === 'add-csat') {
      const csatCustomerProjectId = projectId || projectSeqId;
      if (typeof lazyLoadCsat === 'function' && csatCustomerProjectId) {
        await lazyLoadCsat(csatCustomerProjectId);
      }
      openModal('csatEdit', {});
    }
  };
  const handleCSATDelete = (row) => openModal('csatDelete', row);
  const toRequiredNumber = (value) => {
    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : null;
  };
  const createCsatPayload = (formData, csatRowId, customerId) => ({
    ...(csatRowId ? { row_id: csatRowId } : {}),
    project_id: projectId,
    customer_id: customerId,
    csat_rec_date: formData?.date || null,
    quality_of_service: toRequiredNumber(formData?.serviceQuality),
    return_on_investment: toRequiredNumber(formData?.roi),
    management_communication: toRequiredNumber(formData?.management),
    csat_rating: toRequiredNumber(formData?.overall),
    remarks: String(formData?.feedback || '').trim(),
  });
  const submitCSATEdit = async (formData) => {
    if (isActionInProgress) return;
    const csatRowId = formData?.row_id ?? formData?.id;
    const isEditMode = Boolean(csatRowId);
    if (isEditMode && !canUpdateCsat) {
      snackbarOpen('You do not have permission to edit CSAT.', 'error');
      return;
    }
    if (!isEditMode && !canCreateCsat) {
      snackbarOpen('You do not have permission to add CSAT.', 'error');
      return;
    }
    if (!formData?.date) {
      snackbarOpen('Please select CSAT received date.', 'error');
      return;
    }
    if (dayjs(formData.date).isAfter(dayjs(), 'day')) {
      snackbarOpen('CSAT received date cannot be in the future.', 'error');
      return;
    }
    const customerId = toRequiredNumber(formData?.customerId);
    if (!customerId) {
      snackbarOpen('Please select a valid customer.', 'error');
      return;
    }
    if (!String(formData?.feedback || '').trim()) {
      snackbarOpen('Please enter other feedback.', 'error');
      return;
    }
    if (!projectId || !employeeId || (isEditMode && !csatRowId)) {
      snackbarOpen(
        isEditMode
          ? 'Missing required fields. Please provide CSAT row id and employeeId.'
          : 'Missing required fields. Please provide project id and employeeId.',
        'error',
      );
      return;
    }

    setIsActionInProgress(true);
    try {
      const mappedCsatPayload = createCsatPayload(formData, csatRowId, customerId);
      const requestPayload = {
        project_id: mappedCsatPayload.project_id,
        customer_id: mappedCsatPayload.customer_id,
        csat_rec_date: mappedCsatPayload.csat_rec_date,
        quality_of_service: mappedCsatPayload.quality_of_service,
        return_on_investment: mappedCsatPayload.return_on_investment,
        management_communication: mappedCsatPayload.management_communication,
        csat_rating: mappedCsatPayload.csat_rating,
        remarks: mappedCsatPayload.remarks,
        employeeId,
        employeeName: userProfile?.name,
        email: userProfile?.email,
      };
      const response = isEditMode
        ? await UseAxios(updateProjectCsat(csatRowId, requestPayload))
        : await UseAxios(createProjectCsat(requestPayload));
      assertApiSuccess(response, isEditMode ? 'Failed to update CSAT.' : 'Failed to add CSAT.');
      snackbarOpen(
        isEditMode ? 'CSAT updated successfully.' : 'CSAT added successfully.',
        'success',
      );
      closeModal('csatEdit');
      if (typeof onRefreshData === 'function') onRefreshData();
    } catch (err) {
      snackbarOpen(getErrorMessage(err, isEditMode ? 'Failed to update CSAT.' : 'Failed to add CSAT.'), 'error');
    } finally {
      setIsActionInProgress(false);
    }
  };
  const confirmCSATDelete = async () => {
    const targetRow = modalState.csatDelete.data;
    const csatRowId = targetRow?.row_id ?? targetRow?.id;
    if (isActionInProgress) return;
    if (!csatRowId) {
      snackbarOpen('Missing required fields. Please provide CSAT row id.', 'error');
      return;
    }
    setIsActionInProgress(true);
    try {
      const response = await UseAxios(deleteProjectCsat(csatRowId, {
        employeeId,
        employeeName: userProfile?.name,
        email: userProfile?.email,
      }));
      assertApiSuccess(response, 'Failed to delete CSAT.');
      snackbarOpen('CSAT deleted successfully.', 'success');
      closeModal('csatDelete');
      if (typeof onRefreshData === 'function') onRefreshData();
    } catch (err) {
      snackbarOpen(getErrorMessage(err, 'Failed to delete CSAT.'), 'error');
    } finally {
      setIsActionInProgress(false);
    }
  };

  // Progress handlers
  const handleProgressEdit = (row) => {
    if (!canUpdateProjectProgress) return;
    openModal('progressEdit', row);
  };
  const handleProgressMore = (menuItem) => {
    if (!canCreateProjectProgress) return;
    if (menuItem?.id === 'add-progress') {
      openModal('progressEdit', {});
    }
  };
  const handleProgressDelete = (row) => {
    if (!canDeleteProjectProgress) return;
    openModal('progressDelete', row);
  };
  const submitProgressEdit = async (formData) => {
    if (isActionInProgress) return;
    const progressRowId = formData?.row_id ?? formData?.id;
    const isEditMode = Boolean(progressRowId);
    if (isEditMode && !canUpdateProjectProgress) {
      snackbarOpen('You do not have permission to edit project progress.', 'error');
      return;
    }
    if (!isEditMode && !canCreateProjectProgress) {
      snackbarOpen('You do not have permission to add project progress.', 'error');
      return;
    }
    const resolvedProjectId = modalState.progressEdit.data?.project_id ?? progressData?.[0]?.project_id ?? projectId;
    if (!resolvedProjectId || (isEditMode && !progressRowId)) {
      snackbarOpen(
        isEditMode
          ? 'Missing required fields. Please provide progress row id.'
          : 'Missing required fields. Please provide project id.',
        'error',
      );
      return;
    }
    setIsActionInProgress(true);
    try {
      const mappedProgressPayload = {
        project_id: resolvedProjectId,
        date: formData?.date || null,
        progress_percent: formData?.progress,
        remarks: formData?.remarks || '',
      };
      const response = isEditMode
        ? await UseAxios(updateProjectProgress(progressRowId, mappedProgressPayload, accessToken))
        : await UseAxios(createProjectProgress(mappedProgressPayload, accessToken));
      assertApiSuccess(response, isEditMode ? 'Failed to update progress.' : 'Failed to add progress.');
      snackbarOpen(isEditMode ? 'Progress updated successfully.' : 'Progress added successfully.', 'success');
      closeModal('progressEdit');
      if (typeof onRefreshData === 'function') onRefreshData();
    } catch (err) {
      snackbarOpen(getErrorMessage(err, isEditMode ? 'Failed to update progress.' : 'Failed to add progress.'), 'error');
    } finally {
      setIsActionInProgress(false);
    }
  };
  const confirmProgressDelete = async () => {
    const targetRow = modalState.progressDelete.data;
    const progressRowId = targetRow?.row_id ?? targetRow?.id;
    if (isActionInProgress) return;
    if (!canDeleteProjectProgress) {
      snackbarOpen('You do not have permission to delete project progress.', 'error');
      return;
    }
    if (!progressRowId) {
      snackbarOpen('Missing required fields. Please provide progress row id.', 'error');
      return;
    }
    setIsActionInProgress(true);
    try {
      const response = await UseAxios(deleteProjectProgress(progressRowId, accessToken));
      assertApiSuccess(response, 'Failed to delete progress.');
      snackbarOpen('Progress deleted successfully.', 'success');
      closeModal('progressDelete');
      if (typeof onRefreshData === 'function') onRefreshData();
    } catch (err) {
      snackbarOpen(getErrorMessage(err, 'Failed to delete progress.'), 'error');
    } finally {
      setIsActionInProgress(false);
    }
  };
  const activeProgressRow = modalState.progressEdit.data || {};
  const isProgressEditMode = Boolean(activeProgressRow?.row_id ?? activeProgressRow?.id);
  const activeProjectUnitRow = modalState.projectUnitEdit.data || {};
  const isProjectUnitEditMode = Boolean(activeProjectUnitRow?.row_id ?? activeProjectUnitRow?.id);
  const activeAuditRow = modalState.auditEdit.data || {};
  const isAuditEditMode = Boolean(activeAuditRow?.row_id ?? activeAuditRow?.id);
  const activeRiskRow = modalState.riskEdit.data || {};
  const isRiskEditMode = Boolean(activeRiskRow?.row_id ?? activeRiskRow?.id);
  const activeIssueRow = modalState.issueEdit.data || {};
  const isIssueEditMode = Boolean(activeIssueRow?.row_id ?? activeIssueRow?.id);
  const activeInfoSecRow = modalState.infoSecEdit.data || {};
  const isInfoSecEditMode = Boolean(activeInfoSecRow?.row_id ?? activeInfoSecRow?.id);

  // Effort handlers
  const openBudgetChangeRequestPage = () => {
    if (!projectId) {
      snackbarOpen('Project id is required to open budget change request.', 'error');
      return;
    }
    const params = new URLSearchParams({ projectId: String(projectId) });
    navigate(`/delivery/project-details/budget-change-request?${params.toString()}`);
  };
  const openChangeRequestPage = () => {
    if (!canViewChangeRequest) {
      snackbarOpen('You do not have permission to view change requests.', 'error');
      return;
    }
    if (!projectId) {
      snackbarOpen('Project id is required to open change request.', 'error');
      return;
    }
    const params = new URLSearchParams({ projectId: String(projectId) });
    window.open(`/projects/change-request?${params.toString()}`, '_blank');
  };
  const handleEffortEdit = (row) => openModal('effortEdit', row);
  const handleEffortMore = (menuItem) => {
    if (menuItem?.id === 'resource-loading-plan') {
      handleOpenResourceLoadingPlan();
      return;
    }
    if (menuItem?.id === 'budget-change-request') {
      openBudgetChangeRequestPage();
      return;
    }
    if (menuItem?.id === 'change-request') {
      openChangeRequestPage();
      return;
    }
    if (menuItem?.id !== 'add-update-effort') return;
    if (!canCreateEffortEstimation) {
      snackbarOpen('You do not have permission to add effort estimation.', 'error');
      return;
    }
    openModal('effortEdit', { _entity_type: 'effort_estimation' });
  };
  const handleEffortDelete = (row) => openModal('effortDelete', row);
  const handleProjectUnitAdd = () => {
    if (!canCreateProjectUnit) {
      snackbarOpen('You do not have permission to add project unit.', 'error');
      return;
    }
    openModal('projectUnitEdit', {});
  };
  const handleProjectUnitEdit = (row) => {
    if (!canUpdateProjectUnit) {
      snackbarOpen('You do not have permission to edit project unit.', 'error');
      return;
    }
    openModal('projectUnitEdit', row);
  };
  const handleProjectUnitMore = (menuItem) => {
    if (menuItem?.id === 'budget-change-request') {
      openBudgetChangeRequestPage();
      return;
    }
    if (menuItem?.id === 'resource-loading-plan') {
      handleOpenResourceLoadingPlan();
      return;
    }
    if (menuItem?.id === 'add-project-unit') {
      handleProjectUnitAdd();
    }
  };
  const handleProjectUnitDelete = (row) => {
    if (!canDeleteProjectUnit) {
      snackbarOpen('You do not have permission to delete project unit.', 'error');
      return;
    }
    openModal('projectUnitDelete', row);
  };
  const handleOpenResourceLoadingPlan = useCallback(() => {
    if (!canViewResourceLoadingPlan) {
      snackbarOpen('You do not have permission to view resource loading plan.', 'error');
      return;
    }

    const params = new URLSearchParams();
    if (projectId) params.set('projectId', String(projectId));
    const queryString = params.toString();
    window.open(`/delivery/project-details/resource-loading-plan${queryString ? `?${queryString}` : ''}`, '_blank');
  }, [canViewResourceLoadingPlan, navigate, projectId, snackbarOpen]);
  const submitProjectUnitEdit = async (formData) => {
    if (isActionInProgress) return;

    const unitId = formData?.row_id ?? formData?.id;
    const isEditMode = Boolean(unitId);
    if (isEditMode && !canUpdateProjectUnit) {
      snackbarOpen('You do not have permission to edit project unit.', 'error');
      return;
    }
    if (!isEditMode && !canCreateProjectUnit) {
      snackbarOpen('You do not have permission to add project unit.', 'error');
      return;
    }
    const resolvedProjectId = projectId ?? modalState.projectUnitEdit.data?.projectId ?? modalState.projectUnitEdit.data?.project_id;
    const resourceCategoryId = toRequiredNumber(formData?.resourceCategoryId);
    const quantity = toRequiredNumber(formData?.quantity);
    const unitPrice = toRequiredNumber(formData?.unitPrice);

    if (!resolvedProjectId) {
      snackbarOpen('Missing required fields. Please provide project id.', 'error');
      return;
    }
    if (!resourceCategoryId) {
      snackbarOpen('Please select a valid resource category.', 'error');
      return;
    }
    if (quantity === null) {
      snackbarOpen('Please enter a valid quantity.', 'error');
      return;
    }
    if (unitPrice === null) {
      snackbarOpen('Please enter a valid unit cost.', 'error');
      return;
    }

    setIsActionInProgress(true);
    try {
      const payload = {
        projectId: resolvedProjectId,
        quantity,
        skills: String(formData?.skills || '').trim(),
        resourceCategory: resourceCategoryId,
        startDate: formData?.startDate || null,
        endDate: formData?.endDate || null,
        unitPrice,
        remarks: String(formData?.remarks || '').trim(),
      };
      const response = isEditMode
        ? await UseAxios(updateProjectUnit(unitId, payload))
        : await UseAxios(createProjectUnit(payload));
      assertApiSuccess(response, isEditMode ? 'Failed to update unit.' : 'Failed to add unit.');
      snackbarOpen(isEditMode ? 'Project unit updated successfully.' : 'Project unit added successfully.', 'success');
      closeModal('projectUnitEdit');
      if (typeof onRefreshData === 'function') onRefreshData();
    } catch (err) {
      snackbarOpen(getErrorMessage(err, isEditMode ? 'Failed to update unit.' : 'Failed to add unit.'), 'error');
    } finally {
      setIsActionInProgress(false);
    }
  };
  const confirmProjectUnitDelete = async () => {
    const targetRow = modalState.projectUnitDelete.data;
    const unitId = targetRow?.row_id ?? targetRow?.id ?? targetRow?.unitId;
    if (isActionInProgress) return;
    if (!canDeleteProjectUnit) {
      snackbarOpen('You do not have permission to delete project unit.', 'error');
      return;
    }
    if (!unitId) {
      snackbarOpen('Missing required fields. Please provide project unit id.', 'error');
      return;
    }
    setIsActionInProgress(true);
    try {
      const response = await UseAxios(deleteProjectUnit(unitId));
      assertApiSuccess(response, 'Failed to delete project unit.');
      snackbarOpen('Project unit deleted successfully.', 'success');
      closeModal('projectUnitDelete');
      if (typeof onRefreshData === 'function') onRefreshData();
    } catch (err) {
      snackbarOpen(getErrorMessage(err, 'Failed to delete project unit.'), 'error');
    } finally {
      setIsActionInProgress(false);
    }
  };
  const getEffortEntityType = (row) => row?._entity_type || 'change_request';
  const getEffortRowId = (row) => row?.row_id ?? row?.id;
  const isValidEffortRowId = (rowId) => Number.isFinite(Number(rowId));
  const getInvalidEffortRowMessage = (effortEntityType) => (
    effortEntityType === 'effort_estimation' ? 'Missing or invalid effort row_id. Please use internal effort_id.'
      : 'Missing or invalid CR row_id. Please use internal change request id (cr_id).'
  );
  const isDefined = (value) => value !== null && value !== undefined;
  const isUnsupportedEffortEntityError = (effortEntityType, err) => {
    if (effortEntityType !== 'effort_estimation') return false;
    const errorMessage = String(err?.response?.data?.message || err?.message || '');
    return /entity[_\\s-]*type/i.test(errorMessage);
  };
  const buildEffortUpdatePayload = (formData, effortEntityType, effortRowId) => {
    if (effortEntityType === 'effort_estimation') {
      const isEditMode = isValidEffortRowId(effortRowId);
      return {
        id: formData?.id,
        row_id: effortRowId,
        details: formData?.details || '',
        efforts: formData?.efforts,
        efforts_hours: formData?.efforts,
        estimated_efforts: formData?.efforts,
        ...(isDefined(formData?.category_id) ? { category_id: formData.category_id } : {}),
        ...(isDefined(formData?.category_label) ? { category_label: formData.category_label } : {}),
        ...(isDefined(formData?.custom_category) ? { custom_category: formData.custom_category } : {}),
        ...(isDefined(formData?.type_id) ? { type_id: formData.type_id } : {}),
        ...(!isEditMode ? {
          proposal_id: projectId,
          cr_id: isDefined(formData?.cr_id) ? formData.cr_id : null,
        } : {}),
      };
    }

    return {
      id: formData?.id,
      row_id: effortRowId,
      account_name: formData?.account_name || formData?.crName || '',
      efforts_hour: formData?.efforts,
      ...(isDefined(formData?.efforts_investment) ? { efforts_investment: formData.efforts_investment } : {}),
      ...(isDefined(formData?.customer_approved_status) ? { customer_approved_status: formData.customer_approved_status }
        : {}),
      remarks: formData?.remarks || formData?.details || '',
    };
  };

  const submitEffortEdit = async (formData) => {
    const effortEntityType = getEffortEntityType(formData);
    const effortRowId = getEffortRowId(formData);
    const isEditMode = isValidEffortRowId(effortRowId);
    if (isActionInProgress) return;
    if (isEditMode && !canUpdateEffortEstimation) {
      snackbarOpen('You do not have permission to edit effort estimation.', 'error');
      return;
    }
    if (!isEditMode && !canCreateEffortEstimation) {
      snackbarOpen('You do not have permission to add effort estimation.', 'error');
      return;
    }
    if (!projectSeqId || !employeeId) {
      snackbarOpen(
        isEditMode
          ? 'Missing required fields. Please provide entity_type, row_id, and employeeId.'
          : 'Missing required fields. Please provide entity_type and employeeId.',
        'error',
      );
      return;
    }
    setIsActionInProgress(true);
    try {
      const mappedEffortPayload = buildEffortUpdatePayload(formData, effortEntityType, effortRowId);
      const response = await UseAxios(updateProjectDetails(
        projectSeqId,
        {
          ...getUpdatePayload(mappedEffortPayload, effortEntityType),
          payload: { userInfo: { employeeId } },
        },
      ));
      assertApiSuccess(response, isEditMode ? 'Failed to update effort.' : 'Failed to add effort.');
      snackbarOpen(isEditMode ? 'Effort updated successfully.' : 'Effort added successfully.', 'success');
      closeModal('effortEdit');
      if (typeof onRefreshData === 'function') onRefreshData();
    } catch (err) {
      if (isUnsupportedEffortEntityError(effortEntityType, err)) {
        snackbarOpen('Backend update pending for effort row edits', 'warning');
        return;
      }
      snackbarOpen(getErrorMessage(err, isEditMode ? 'Failed to update effort.' : 'Failed to add effort.'), 'error');
    } finally {
      setIsActionInProgress(false);
    }
  };
  const confirmEffortDelete = async () => {
    const targetRow = modalState.effortDelete.data;
    const effortEntityType = getEffortEntityType(targetRow);
    const effortRowId = getEffortRowId(targetRow);
    if (isActionInProgress) return;
    if (!canDeleteEffortEstimation) {
      snackbarOpen('You do not have permission to delete effort estimation.', 'error');
      return;
    }
    if (!projectSeqId || !employeeId || !isValidEffortRowId(effortRowId)) {
      snackbarOpen(getInvalidEffortRowMessage(effortEntityType), 'error');
      return;
    }
    setIsActionInProgress(true);
    try {
      const response = await UseAxios(deleteProjectDetails(projectSeqId, getDeletePayload(targetRow, effortEntityType)));
      assertApiSuccess(response, 'Failed to delete effort.');
      snackbarOpen('Effort deleted successfully.', 'success');
      closeModal('effortDelete');
      if (typeof onRefreshData === 'function') onRefreshData();
    } catch (err) {
      if (isUnsupportedEffortEntityError(effortEntityType, err)) {
        snackbarOpen('Backend update pending for effort row edits', 'warning');
        return;
      }
      snackbarOpen(getErrorMessage(err, 'Failed to delete effort.'), 'error');
    } finally {
      setIsActionInProgress(false);
    }
  };

  // Audit handlers
  const handleAuditEdit = (row) => openModal('auditEdit', row);
  const handleAuditMore = (menuItem) => {
    if (!canCreateAuditScore) return;
    if (menuItem?.id !== 'add-audit-score') return;
    const historyRows = Array.isArray(auditScoreData?.history) ? auditScoreData.history : [];
    const readAuditNameId = (field) => {
      const sourceRow = historyRows.find((row) => (
        row?.[field] !== null && row?.[field] !== undefined && row?.[field] !== ''
      ));
      return sourceRow?.[field] ?? null;
    };
    openModal('auditEdit', {
      processDate: '',
      processScore: '',
      qualityScore: '',
      technologyScore: '',
      processAuditNameId: readAuditNameId('processAuditNameId'),
      qualityAuditNameId: readAuditNameId('qualityAuditNameId'),
      technologyAuditNameId: readAuditNameId('technologyAuditNameId'),
    });
  };
  const handleAuditDelete = (row) => openModal('auditDelete', row);
  const toAuditIsoDate = (value) => {
    if (!value) return '';
    const normalized = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return normalized;
    if (/^\d{2}-\d{2}-\d{4}$/.test(normalized)) {
      const [day, month, year] = normalized.split('-');
      return `${year}-${month}-${day}`;
    }
    if (/^\d{2}-[A-Za-z]{3}-\d{4}$/.test(normalized)) {
      const [day, monthName, year] = normalized.split('-');
      const month = MONTH_NAMES_SHORT.findIndex((item) => item.toLowerCase() === monthName.toLowerCase());
      if (month === -1) return '';
      return `${year}-${String(month + 1).padStart(2, '0')}-${day}`;
    }
    return '';
  };
  const isValidAuditScoreInput = (value) => {
    if (value === null || value === undefined || value === '') return true;
    const trimmed = String(value).trim();
    const regex = /^(\d(?:\.[0-9]{0,2})?|10|NA|-)$/i;
    return regex.test(trimmed);
  };
  const normalizeAuditScore = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const trimmed = String(value).trim();
    if (trimmed.toUpperCase() === 'NA') return 'NA';
    if (trimmed === '-') return '-';
    return trimmed;
  };
  const getAuditRequiredFieldsMessage = (isEditMode) => (
    isEditMode
      ? 'Missing required fields. Please provide entity_type, row_id, and employeeId.'
      : 'Missing required fields. Please provide entity_type and employeeId.'
  );
  const hasAnyAuditScore = (scores = {}) => (
    Boolean(scores.processCompliance || scores.quality || scores.technology)
  );
  const getAuditValidationError = ({ projectSeqIdValue, employeeIdValue, isEditMode, auditRowId, auditDate, scores }) => {
    if (!projectSeqIdValue || !employeeIdValue || (isEditMode && !auditRowId)) {
      return getAuditRequiredFieldsMessage(isEditMode);
    }
    if (!hasAnyAuditScore(scores)) {
      return 'Please enter at least one audit score.';
    }
    if (
      !isValidAuditScoreInput(scores.processCompliance)
      || !isValidAuditScoreInput(scores.quality)
      || !isValidAuditScoreInput(scores.technology)
    ) {
      return 'Only 0 to 10 with 2 decimal places, NA,“-” values are allowed in this field';
    }
    if (!isEditMode && !auditDate) {
      return 'Please select audit date.';
    }
    return '';
  };
  const buildAuditPayload = ({ isEditMode, auditRowId, auditDate, scores }) => {
    const payload = {
      entity_type: 'audit',
      processCompliance: scores.processCompliance,
      quality: scores.quality,
      technology: scores.technology,
      payload: { userInfo: { employeeId } },
    };
    if (isEditMode) {
      payload.row_id = auditRowId;
    }
    if (auditDate) {
      payload.audit_date = auditDate;
    }
    return payload;
  };
  const submitAuditEdit = async (formData) => {
    const auditRowId = formData?.row_id ?? formData?.id;
    const isEditMode = Boolean(auditRowId);
    if (isEditMode && !canUpdateAuditScore) {
      snackbarOpen('You do not have permission to edit audit score.', 'error');
      return;
    }
    if (!isEditMode && !canCreateAuditScore) {
      snackbarOpen('You do not have permission to add audit score.', 'error');
      return;
    }
    const auditDate = toAuditIsoDate(formData?.date);
    const scores = {
      processCompliance: normalizeAuditScore(formData?.processCompliance),
      quality: normalizeAuditScore(formData?.quality),
      technology: normalizeAuditScore(formData?.technology),
    };
    if (isActionInProgress) return;
    const validationError = getAuditValidationError({
      projectSeqIdValue: projectSeqId,
      employeeIdValue: employeeId,
      isEditMode,
      auditRowId,
      auditDate,
      scores,
    });
    if (validationError) {
      snackbarOpen(validationError, 'error');
      return;
    }
    setIsActionInProgress(true);
    try {
      const mappedAuditPayload = buildAuditPayload({ isEditMode, auditRowId, auditDate, scores });
      const response = await UseAxios(updateProjectDetails(projectSeqId, mappedAuditPayload));
      assertApiSuccess(response, isEditMode ? 'Failed to update audit.' : 'Failed to add audit.');
      snackbarOpen(isEditMode ? 'Audit updated successfully.' : 'Audit score added successfully.', 'success');
      closeModal('auditEdit');
      if (typeof onRefreshData === 'function') onRefreshData();
    } catch (err) {
      snackbarOpen(getErrorMessage(err, isEditMode ? 'Failed to update audit.' : 'Failed to add audit.'), 'error');
    } finally {
      setIsActionInProgress(false);
    }
  };
  const confirmAuditDelete = async () => {
    await confirmEntityDelete({
      modalKey: 'auditDelete',
      entityType: 'audit',
      successMessage: 'Audit deleted successfully.',
      failureMessage: 'Failed to delete audit.',
    });
  };

  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (autoOpenedRef.current) return;

    const queryParams = new URLSearchParams(location.search);
    const editId = queryParams.get('editId');
    const editType = queryParams.get('editType');

    if (editId && editType) {
      let targetTab = 'Risk';
      let rowsToSearch = [];
      let modalKey = '';

      if (editType === 'issue') {
        targetTab = 'Issue';
        rowsToSearch = issueRows;
        modalKey = 'issueEdit';
      } else if (editType === 'infosec' || editType === 'infosec_risk') {
        targetTab = 'InfoSec Risk';
        rowsToSearch = infoSecRows;
        modalKey = 'infoSecEdit';
      } else {
        targetTab = 'Risk';
        rowsToSearch = riskRows;
        modalKey = 'riskEdit';
      }

      if (activeRiskTab !== targetTab && riskVisibleTabs.includes(targetTab)) {
        setActiveRiskTab(targetTab);
      }

      if (rowsToSearch && rowsToSearch.length > 0) {
        const matchingRow = rowsToSearch.find(
          (r) => String(r.row_id) === String(editId) || String(r.id) === String(editId),
        );

        if (matchingRow) {
          openModal(modalKey, matchingRow);
          autoOpenedRef.current = true;
          window.setTimeout(() => {
            const targetElement = document.getElementById('project-details-section-risk');
            if (targetElement) {
              targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 100);
        }
      }
    }
  }, [location.search, riskRows, issueRows, infoSecRows, riskVisibleTabs, activeRiskTab]);

  // Risk handlers
  const handleRiskEdit = (row) => {
    openModal('riskEdit', row);
  };
  const handleRiskMore = (menuItem) => {
    if (!menuItem?.id) return;
    if (menuItem.id === 'risk-report-mis') {
      window.open(`/mis/risk-register?projectId=${projectId}&tab=risk`, '_blank');
      return;
    }
    if (menuItem.id === 'issue-report-mis') {
      window.open(`/mis/risk-register?projectId=${projectId}&tab=issue`, '_blank');
      return;
    }
    if (menuItem.id === 'infosec-report-mis') {
      window.open(`/mis/risk-register?projectId=${projectId}&tab=infosec`, '_blank');
      return;
    }
    if (menuItem.id === 'add-risk') {
      if (!canCreateRisk) return;
      if (typeof lazyLoadRisk === 'function') lazyLoadRisk();
      openModal('riskEdit', {});
      return;
    }
    if (menuItem.id === 'add-issues') {
      if (!canCreateIssue) return;
      if (typeof lazyLoadRisk === 'function') lazyLoadRisk();
      openModal('issueEdit', {});
      return;
    }
    if (menuItem.id === 'add-infosec-risk') {
      if (!canCreateInfoSecRisk) return;
      if (typeof lazyLoadRisk === 'function') lazyLoadRisk();
      openModal('infoSecEdit', {});
    }
  };
  const handleRiskDelete = (row) => {
    openModal('riskDelete', row);
  };
  const toNumberOrNull = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };
  const toYyyyMmDd = (value) => {
    if (!value) return '';
    const normalized = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return normalized;
    if (/^\d{2}-\d{2}-\d{4}$/.test(normalized)) {
      const [day, month, year] = normalized.split('-');
      return `${year}-${month}-${day}`;
    }
    if (/^\d{2}-[A-Za-z]{3}-\d{4}$/.test(normalized)) {
      const [day, monthName, year] = normalized.split('-');
      const normalizedMonthName = `${monthName.charAt(0).toUpperCase()}${monthName.slice(1).toLowerCase()}`;
      const monthIndex = MONTH_INDEX_MAP[normalizedMonthName];
      if (monthIndex === undefined) return normalized;
      const month = String(monthIndex + 1).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    const parsedDate = new Date(normalized);
    if (Number.isNaN(parsedDate.getTime())) return normalized;
    const year = parsedDate.getFullYear();
    const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
    const day = String(parsedDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const getMissingIdFields = (fields) => fields
    .filter(([, value]) => value === null || value === undefined)
    .map(([label]) => label);
  const isPresentValue = (value) => value !== null && value !== undefined && value !== '';
  const hasAnyInfoSecField = (entries = []) => entries.some(([, value]) => isPresentValue(value));
  const getInfoSecPayloadMeta = (formData = {}) => {
    const infoSecRowId = formData?.row_id ?? formData?.id;
    const isEditMode = Boolean(infoSecRowId);
    const entityType = String(formData?.entity_type || formData?._entity_type || 'infosec_risk').toLowerCase();
    const assetCategoryId = toNumberOrNull(
      formData?.assetCategoryOption?.id
      ?? formData?.assetCategoryId
      ?? formData?.categoryId,
    );
    const impactId = toNumberOrNull(formData?.impactOption?.id ?? formData?.impactId);
    const riskStatusId = toNumberOrNull(formData?.riskStatusOption?.id ?? formData?.riskStatusId);
    const likelihoodId = toNumberOrNull(
      formData?.likelihoodOption?.id
      ?? formData?.likelihoodId
      ?? formData?.liklihoodId,
    );
    const mitigationStrategyId = toNumberOrNull(
      formData?.mitigationOption?.id
      ?? formData?.mitigationStrategyId
      ?? formData?.mitigationStatergyId,
    );
    const totalAssetValue = toNumberOrNull(formData?.totalAssetValue);
    const infoSecDescription = String(formData?.description || '').trim();
    const targetDate = toYyyyMmDd(formData?.targetDate);
    const revisedTargetDate = toYyyyMmDd(formData?.revisedTargetDate);
    return {
      infoSecRowId,
      isEditMode,
      entityType,
      assetCategoryId,
      impactId,
      riskStatusId,
      likelihoodId,
      mitigationStrategyId,
      totalAssetValue,
      infoSecDescription,
      targetDate,
      revisedTargetDate,
      owner: formData?.owner || '',
      measuresTaken: formData?.measures || '',
    };
  };
  const buildInfoSecPayload = (meta) => {
    const payload = {
      entity_type: meta.entityType,
      infosec_description: meta.infoSecDescription,
      asset_category_id: meta.assetCategoryId,
      liklihood_id: meta.likelihoodId,
      impact_id: meta.impactId,
      risk_status_id: meta.riskStatusId,
      mitigation_statergy_id: meta.mitigationStrategyId,
      measures_taken: meta.measuresTaken,
      owner: meta.owner,
      target_date: meta.targetDate,
      revised_target_date: meta.revisedTargetDate,
      payload: { userInfo: { employeeId } },
    };
    if (meta.isEditMode) {
      payload.row_id = meta.infoSecRowId;
    }
    if (meta.totalAssetValue !== null) {
      payload.total_asset_value = meta.totalAssetValue;
    }
    return payload;
  };
  const getInfoSecValidationError = ({
    projectSeqIdValue,
    employeeIdValue,
    entityType,
    hasPayloadEmployeeId,
    infoSecDescription,
    targetDate,
    revisedTargetDate,
    isEditMode,
    totalAssetValue,
    hasAtLeastOneInfoSecField,
    missingIdFields,
  }) => {
    if (!projectSeqIdValue || !employeeIdValue) {
      return 'Missing project/employee details for update.';
    }
    if (entityType !== 'infosec_risk' && entityType !== 'infosec') {
      return 'Invalid entity_type. Allowed values: infosec_risk or infosec.';
    }
    if (!hasPayloadEmployeeId) {
      return 'Missing payload.userInfo.employeeId for infosec risk update.';
    }
    if (!infoSecDescription || /^\d+$/.test(infoSecDescription)) {
      return 'InfoSec description must be meaningful text (not only numbers).';
    }
    if (!targetDate) {
      return 'Target date is required for infosec risk update.';
    }
    if (targetDate && !isYyyyMmDdDate(targetDate)) {
      return 'Target date must be in YYYY-MM-DD format.';
    }
    if (revisedTargetDate && !isYyyyMmDdDate(revisedTargetDate)) {
      return 'Revised target date must be in YYYY-MM-DD format.';
    }
    if (!isEditMode && totalAssetValue === null) {
      return 'Total asset value is required for adding infosec risk.';
    }
    if (!hasAtLeastOneInfoSecField) {
      return 'Provide at least one valid InfoSec field for submission.';
    }
    if (missingIdFields.length > 0) {
      return `Missing required infosec risk IDs: ${missingIdFields.join(', ')}.`;
    }
    return '';
  };

  const submitRiskEdit = async (formData) => {
    const riskRowId = formData?.row_id ?? formData?.id;
    const isEditMode = Boolean(riskRowId);
    if (isEditMode && !canUpdateRisk) {
      snackbarOpen('You do not have permission to edit risk.', 'error');
      return;
    }
    if (!isEditMode && !canCreateRisk) {
      snackbarOpen('You do not have permission to add risk.', 'error');
      return;
    }
    const mappedRiskPayload = {
      entity_type: 'risk',
      ...(isEditMode ? { row_id: riskRowId } : {}),
      risk_description: formData?.description || '',
      impact_id: toNumberOrNull(formData?.impactId),
      risk_status_id: toNumberOrNull(formData?.riskStatusId),
      category_id: toNumberOrNull(formData?.categoryId),
      target_date: toYyyyMmDd(formData?.targetDate),
      revised_target_date: toYyyyMmDd(formData?.revisedTargetDate),
      owner: formData?.owner || '',
      mitigation_strategy_id: toNumberOrNull(formData?.mitigationStrategyId),
      likelihood_id: toNumberOrNull(formData?.likelihoodId),
      risk_treatment: formData?.measures || '',
      payload: { userInfo: { employeeId } },
    };
    const missingIdFields = getMissingIdFields([
      ['impact', mappedRiskPayload.impact_id],
      ['risk status', mappedRiskPayload.risk_status_id],
      ['risk category', mappedRiskPayload.category_id],
      ['mitigation strategy', mappedRiskPayload.mitigation_strategy_id],
      ['likelihood', mappedRiskPayload.likelihood_id],
    ]);
    if (isActionInProgress) return;
    if (!projectSeqId || !employeeId) {
      snackbarOpen('Missing project/employee details for update.', 'error');
      return;
    }
    if (missingIdFields.length > 0) {
      const missingText = missingIdFields.join(', ');
      snackbarOpen(`Missing required risk IDs: ${missingText}.`, 'error');
      return;
    }
    setIsActionInProgress(true);
    try {
      const response = await UseAxios(updateProjectDetails(projectSeqId, mappedRiskPayload));
      assertApiSuccess(response, isEditMode ? 'Failed to update risk.' : 'Failed to add risk.');
      snackbarOpen(isEditMode ? 'Risk updated successfully.' : 'Risk added successfully.', 'success');
      closeModal('riskEdit');
      if (typeof onRefreshData === 'function') onRefreshData();
    } catch (err) {
      snackbarOpen(getErrorMessage(err, isEditMode ? 'Failed to update risk.' : 'Failed to add risk.'), 'error');
    } finally {
      setIsActionInProgress(false);
    }
  };
  const confirmRiskDelete = async () => {
    const targetRow = modalState.riskDelete.data;
    const riskRowId = targetRow?.row_id ?? targetRow?.id;
    if (isActionInProgress) return;
    if (!projectSeqId || !employeeId || !riskRowId) {
      snackbarOpen('Missing project/employee details for delete.', 'error');
      return;
    }
    setIsActionInProgress(true);
    try {
      const response = await UseAxios(deleteProjectDetails(projectSeqId, getDeletePayload({
        row_id: riskRowId,
      }, 'risk')));
      assertApiSuccess(response, 'Failed to delete risk.');
      snackbarOpen('Risk deleted successfully.', 'success');
      closeModal('riskDelete');
      if (typeof onRefreshData === 'function') onRefreshData();
    } catch (err) {
      snackbarOpen(getErrorMessage(err, 'Failed to delete risk.'), 'error');
    } finally {
      setIsActionInProgress(false);
    }
  };

  // Issue handlers
  const handleIssueEdit = (row) => {
    openModal('issueEdit', row);
  };
  const handleIssueDelete = (row) => {
    openModal('issueDelete', row);
  };
  const submitIssueEdit = async (formData) => {
    const issueRowId = formData?.row_id ?? formData?.id;
    const isEditMode = Boolean(issueRowId);
    if (isEditMode && !canUpdateIssue) {
      snackbarOpen('You do not have permission to edit issue.', 'error');
      return;
    }
    if (!isEditMode && !canCreateIssue) {
      snackbarOpen('You do not have permission to add issue.', 'error');
      return;
    }
    const mappedIssuePayload = {
      entity_type: 'issue',
      ...(isEditMode ? { row_id: issueRowId } : {}),
      issue_description: formData?.description || '',
      issue_status_id: toNumberOrNull(formData?.issueStatusId),
      issue_category_id: toNumberOrNull(formData?.categoryId),
      impact_id: toNumberOrNull(formData?.impactId),
      assigned_to: formData?.assignedTo || '',
      target_date: toYyyyMmDd(formData?.targetDate),
      revised_target_date: toYyyyMmDd(formData?.revisedTargetDate),
      status_update: formData?.statusUpdates || '',
      payload: { userInfo: { employeeId } },
    };
    const missingIdFields = getMissingIdFields([
      ['issue status', mappedIssuePayload.issue_status_id],
      ['issue category', mappedIssuePayload.issue_category_id],
    ]);
    if (isActionInProgress) return;
    if (!projectSeqId || !employeeId) {
      snackbarOpen('Missing project/employee details for update.', 'error');
      return;
    }
    if (missingIdFields.length > 0) {
      const missingText = missingIdFields.join(', ');
      snackbarOpen(`Missing required issue IDs: ${missingText}.`, 'error');
      return;
    }
    setIsActionInProgress(true);
    try {
      const response = await UseAxios(updateProjectDetails(projectSeqId, mappedIssuePayload));
      assertApiSuccess(response, isEditMode ? 'Failed to update issue.' : 'Failed to add issue.');
      snackbarOpen(isEditMode ? 'Issue updated successfully.' : 'Issue added successfully.', 'success');
      closeModal('issueEdit');
      if (typeof onRefreshData === 'function') onRefreshData();
    } catch (err) {
      snackbarOpen(getErrorMessage(err, isEditMode ? 'Failed to update issue.' : 'Failed to add issue.'), 'error');
    } finally {
      setIsActionInProgress(false);
    }
  };
  const confirmIssueDelete = async () => {
    const targetRow = modalState.issueDelete.data;
    const issueRowId = targetRow?.row_id ?? targetRow?.id;
    if (isActionInProgress) return;
    if (!projectSeqId || !employeeId || !issueRowId) {
      snackbarOpen('Missing project/employee details for delete.', 'error');
      return;
    }
    setIsActionInProgress(true);
    try {
      const response = await UseAxios(deleteProjectDetails(projectSeqId, getDeletePayload({
        row_id: issueRowId,
      }, 'issue')));
      assertApiSuccess(response, 'Failed to delete issue.');
      snackbarOpen('Issue deleted successfully.', 'success');
      closeModal('issueDelete');
      if (typeof onRefreshData === 'function') onRefreshData();
    } catch (err) {
      snackbarOpen(getErrorMessage(err, 'Failed to delete issue.'), 'error');
    } finally {
      setIsActionInProgress(false);
    }
  };

  // InfoSec handlers
  const handleInfoSecEdit = (row) => {
    openModal('infoSecEdit', row);
  };
  const handleInfoSecDelete = (row) => {
    openModal('infoSecDelete', row);
  };
  const submitInfoSecEdit = async (formData) => {
    const meta = getInfoSecPayloadMeta(formData);
    if (meta.isEditMode && !canUpdateInfoSecRisk) {
      snackbarOpen('You do not have permission to edit infosec risk.', 'error');
      return;
    }
    if (!meta.isEditMode && !canCreateInfoSecRisk) {
      snackbarOpen('You do not have permission to add infosec risk.', 'error');
      return;
    }
    const infoSecApiFieldEntries = [
      ['infosec_description', meta.infoSecDescription],
      ['asset_category_id', meta.assetCategoryId],
      ['impact_id', meta.impactId],
      ['risk_status_id', meta.riskStatusId],
      ['liklihood_id', meta.likelihoodId],
      ['mitigation_statergy_id', meta.mitigationStrategyId],
      ['owner', meta.owner],
      ['target_date', meta.targetDate],
      ['revised_target_date', meta.revisedTargetDate],
      ['measures_taken', meta.measuresTaken],
      ['total_asset_value', meta.totalAssetValue],
    ];
    const hasAtLeastOneInfoSecField = hasAnyInfoSecField(infoSecApiFieldEntries);
    const mappedInfoSecPayload = buildInfoSecPayload(meta);
    const missingIdFields = getMissingIdFields([
      ['impact', mappedInfoSecPayload.impact_id],
      ['risk status', mappedInfoSecPayload.risk_status_id],
      ['asset category', mappedInfoSecPayload.asset_category_id],
      ['mitigation strategy', mappedInfoSecPayload.mitigation_statergy_id],
      ['likelihood', mappedInfoSecPayload.liklihood_id],
    ]);
    if (isActionInProgress) return;
    const validationError = getInfoSecValidationError({
      projectSeqIdValue: projectSeqId,
      employeeIdValue: employeeId,
      entityType: meta.entityType,
      hasPayloadEmployeeId: Boolean(mappedInfoSecPayload?.payload?.userInfo?.employeeId),
      infoSecDescription: meta.infoSecDescription,
      targetDate: meta.targetDate,
      revisedTargetDate: meta.revisedTargetDate,
      isEditMode: meta.isEditMode,
      totalAssetValue: meta.totalAssetValue,
      hasAtLeastOneInfoSecField,
      missingIdFields,
    });
    if (validationError) {
      snackbarOpen(validationError, 'error');
      return;
    }
    setIsActionInProgress(true);
    try {
      const response = await UseAxios(updateProjectDetails(projectSeqId, mappedInfoSecPayload));
      assertApiSuccess(response, meta.isEditMode ? 'Failed to update infosec risk.' : 'Failed to add infosec risk.');
      snackbarOpen(
        meta.isEditMode ? 'Infosec risk updated successfully.' : 'Infosec risk added successfully.',
        'success',
      );
      closeModal('infoSecEdit');
      if (typeof onRefreshData === 'function') onRefreshData();
    } catch (err) {
      snackbarOpen(
        getErrorMessage(err, meta.isEditMode ? 'Failed to update infosec risk.' : 'Failed to add infosec risk.'),
        'error',
      );
    } finally {
      setIsActionInProgress(false);
    }
  };
  const confirmInfoSecDelete = async () => {
    const targetRow = modalState.infoSecDelete.data;
    const infoSecRowId = targetRow?.row_id ?? targetRow?.id;
    if (isActionInProgress) return;
    if (!projectSeqId || !employeeId || !infoSecRowId) {
      snackbarOpen('Missing project/employee details for delete.', 'error');
      return;
    }
    setIsActionInProgress(true);
    try {
      const response = await UseAxios(deleteProjectDetails(projectSeqId, getDeletePayload({
        row_id: infoSecRowId,
      }, 'infosec_risk')));
      assertApiSuccess(response, 'Failed to delete infosec risk.');
      snackbarOpen('Infosec risk deleted successfully.', 'success');
      closeModal('infoSecDelete');
      if (typeof onRefreshData === 'function') onRefreshData();
    } catch (err) {
      snackbarOpen(getErrorMessage(err, 'Failed to delete infosec risk.'), 'error');
    } finally {
      setIsActionInProgress(false);
    }
  };

  const handleDialogClose = () => {
    setIsUpdateDialogOpen(false);
    setEditResourceData(null);
  };

  const handleResourceUpdate = async () => {
    if (isActionInProgress) return;

    if (!canUpdateResourceAllocationDetails) {
      snackbarOpen('You do not have permission to update resource allocation details.', 'error');
      return;
    }

    const allocationValue = editResourceData?.allocation;
    const hasAllocationValue = allocationValue !== null
      && allocationValue !== undefined
      && String(allocationValue).trim() !== '';
    const allocationNumber = Number(allocationValue);

    if (!editResourceData?.isRollOff && !hasAllocationValue) {
      snackbarOpen('Please enter allocation percentage.', 'error');
      return;
    }

    if (
      !editResourceData?.isRollOff
      && (!Number.isFinite(allocationNumber) || allocationNumber <= 0 || allocationNumber > 100)
    ) {
      snackbarOpen('Allocation percentage must be greater than 0 and less than or equal to 100.', 'error');
      return;
    }

    const normalizedStartDate = normalizeDateForApi(editResourceData?.startDate);
    const normalizedEndDate = normalizeDateForApi(editResourceData?.endDate);

    if (!editResourceData?.isRollOff && !normalizedStartDate) {
      snackbarOpen('Please select a start date.', 'error');
      return;
    }

    if (!normalizedEndDate) {
      snackbarOpen('Please select an end date.', 'error');
      return;
    }

    if (
      !editResourceData?.isRollOff
      && dayjs(normalizedStartDate).startOf('day')
        .isBefore(today)
    ) {
      snackbarOpen('Start date cannot be before today.', 'error');
      return;
    }

    if (!editResourceData?.isRollOff && normalizedEndDate && normalizedStartDate) {
      const endDate = dayjs(normalizedEndDate);
      const startDate = dayjs(normalizedStartDate);
      if (endDate.isBefore(startDate)) { // end must be after start
        snackbarOpen('End date must be after start date.', 'error');
        return;
      }
    }

    if (editResourceData?.isRollOff && !String(editResourceData?.comments || '').trim()) {
      snackbarOpen('Please enter comments for roll-off request.', 'error');
      return;
    }

    setIsActionInProgress(true);
    try {
      const payload = {
        project_id: Number(projectId || editResourceData?.projectId),
        start_date: editResourceData?.isRollOff ? normalizedEndDate : normalizedStartDate,
        end_date: normalizedEndDate,
        skills: '',
        remarks: buildLegacyAllocationChangeRemark({
          employeeId: editResourceData?.employeeId,
          name: editResourceData?.name,
          allocation: editResourceData?.currentAllocation ?? editResourceData?.allocation,
          startDate: editResourceData?.startDateValue || editResourceData?.startDate,
          endDate: editResourceData?.endDateValue || editResourceData?.endDate,
          comments: editResourceData?.comments,
          isRollOff: editResourceData?.isRollOff,
        }),
        resource_no: 1,
        allocation_per: Number(
          editResourceData?.isRollOff
            ? (editResourceData?.currentAllocation ?? editResourceData?.allocation)
            : editResourceData?.allocation,
        ) || 0,
        resource_type_id: toOptionalPositiveNumber(editResourceData?.resourceTypeId),
        vertical: projectVertical || '',
        experience: '',
        good_skills: '',
      };

      const response = await createRequest(payload);
      assertApiSuccess(response, 'Failed to create staffing request.');
      snackbarOpen(
        editResourceData?.isRollOff
          ? 'Roll-off staffing request created successfully.'
          : 'Staffing request created successfully.',
        'success',
      );
      setIsUpdateDialogOpen(false);
      setEditResourceData(null);

      if (typeof onRefreshData === 'function') onRefreshData();
    } catch (err) {
      snackbarOpen(getErrorMessage(err, 'Failed to create staffing request.'), 'error');
    } finally {
      setIsActionInProgress(false);
    }
  };

  return (
    <>
      {isUpdateDialogOpen && editResourceData && (
        <Dialog
          className="resource-allocation-update-dialog"
          header="Change Allocation"
          style={{ width: '50vw', maxWidth: '95vw' }}
          breakpoints={{
            '960px': '75vw',
            '768px': '95vw',
            '560px': '100vw',
          }}
          modal
          visible={isUpdateDialogOpen}
          onHide={handleDialogClose}
          blockScroll={true}
          footer={(
            <div className="custom-dialog-footer">
              <button
                type="button"
                className="p-button p-component p-button-secondary"
                onClick={handleDialogClose}
              >
                Cancel
              </button>

              {canUpdateResourceAllocationDetails ? (
                <button
                  type="button"
                  className="p-button p-component p-button-primary"
                  disabled={isFullyLocked}
                  onClick={handleResourceUpdate}
                >
                  Update
                </button>
              ) : (
                <div className="update-btn-wrapper">
                  <button
                    type="button"
                    className="p-button p-component p-button-primary update-btn-disabled"
                    disabled
                    tabIndex={-1}
                  >
                    Update
                  </button>
                  <span className="update-tooltip">
                    You do not have permission to update resource allocation details.
                  </span>
                </div>
              )}
            </div>
          )}
        >
          {isFullyLocked && (
            <div className="form-info-banner form-info-banner--warning">
              Resource is already moved out. No further edits are allowed.
            </div>
          )}

          {isPartiallyLocked && (
            <div className="form-info-banner form-info-banner--info">
              Resource is moved out. You can only extend the end date or add comments.
            </div>
          )}
          <div className="details-wrapper">
            <h3>Employee Details</h3>

            <span>
              {editResourceData?.employeeId} - {editResourceData?.name}
            </span>
          </div>

          <div className="resource-allocation-form-grid">
            <div className="form-field">
              <label>
                Allocation %
                <span className="required-asterisk">*</span>
              </label>

              <input
                type="number"
                className="p-inputtext"
                value={editResourceData?.allocation ?? ''}
                placeholder="Enter allocation"
                step="0.1"
                min={0}
                max={100}
                disabled={disableAllocationField}
                onChange={(e) => {
                  const { value } = e.target;
                  if (value === '') {
                    setEditResourceData((prev) => ({
                      ...prev,
                      allocation: value,
                    }));
                    return;
                  }

                  const numericValue = Number(value);
                  if (Number.isFinite(numericValue) && numericValue >= 0 && numericValue <= 100) {
                    setEditResourceData((prev) => ({
                      ...prev,
                      allocation: value,
                    }));
                  }
                }}
              />
            </div>

            <div className="form-field">
              <CustomDatePicker
                label="Start Date"
                value={getValidDayjs(editResourceData?.startDate)}
                onChange={(value) => {
                  setEditResourceData((prev) => ({
                    ...prev,
                    startDate: normalizeDateForApi(value),
                  }));
                }}
                isDisabled={disableStartDateField}
                displayFormat="DD-MM-YYYY"
                minDate={getCurrentDateFormatted()}
              />
            </div>

            <div className="form-field">
              <CustomDatePicker
                label="End Date"
                value={getValidDayjs(editResourceData?.endDate)}
                defaultDate={getCurrentDateFormatted()}
                isDisabled={disableEndDateField}
                onChange={(value) => {
                  setEditResourceData((prev) => ({
                    ...prev,
                    endDate: normalizeDateForApi(value),
                  }));
                }}
                displayFormat="DD-MM-YYYY"
                minDate={dayjs().startOf('day')}
              />
            </div>

            <div className="form-field full-width">
              <label>Comments</label>

              <textarea
                className="comment-text-area"
                value={editResourceData?.comments || ''}
                placeholder="Enter comments"
                disabled={disableCommentsField}
                onChange={(e) => {
                  setEditResourceData((prev) => ({
                    ...prev,
                    comments: e.target.value,
                  }));
                }}
              />
            </div>

            <div className="form-field checkbox-field">
              <div className="checkbox-wrapper">
                <input
                  type="checkbox"
                  id="rolloff-checkbox"
                  checked={Boolean(editResourceData?.isRollOff)}
                  onChange={(e) => {
                    setEditResourceData((prev) => ({
                      ...prev,
                      isRollOff: e.target.checked,
                    }));
                  }}
                />

                <label htmlFor="rolloff-checkbox">Roll-off</label>
              </div>
            </div>
          </div>
        </Dialog>
      )}
      <div className="expandable-sections">
        {canViewCsat ? (
          <CsatSection
            expanded={expandedSections.csat}
            onToggle={() => onToggleSection('csat')}
            onDownload={handleCsatDownload}
            onMore={handleCSATMore}
            onMoreMenuItems={csatMoreMenuItems}
            showMoreAction={canCreateCsat || canViewCsatCustomerDetails}
            showActions={canShowCsatActions}
            canEditCsat={canUpdateCsat}
            canDeleteCsat={canDeleteCsat}
            csatOverallTotal={csatOverallTotal}
            csatOverallAverage={csatOverallAverage}
            csatData={csatData}
            csatCurrentYearTotal={csatCurrentYearTotal}
            csatCurrentYearAverage={csatCurrentYearAverage}
            csatScore={csatScore}
            csatDate={csatDate}
            csatLastReceived={csatLastReceived}
            getSortClass={getSortClass}
            csatSort={csatSort}
            getAriaSort={getAriaSort}
            handleCsatSort={handleCsatSort}
            csatTableData={csatSortedData}
            projectName={projectName}
            resolveCsatCustomerName={resolveCsatCustomerName}
            handleCSATEdit={handleCSATEdit}
            handleCSATDelete={handleCSATDelete}
            isCsatEmpty={isCsatEmpty}
            isLoading={csatCustomersLoading}
          />
        ) : null}

        {canViewProjectProgress && projectTypeVariant === 'fp' ? (
          <ProgressSection
            expanded={expandedSections.progress}
            onToggle={() => onToggleSection('progress')}
            onDownload={handleProgressDownload}
            onMoreMenuItems={progressMoreMenuItems}
            onMore={handleProgressMore}
            showMoreAction={canCreateProjectProgress}
            showActions={canShowProjectProgressActions}
            canEditProgress={canUpdateProjectProgress}
            canDeleteProgress={canDeleteProjectProgress}
            getProgressSortClass={getProgressSortClass}
            progressSort={progressSort}
            getAriaSort={getAriaSort}
            handleProgressSort={handleProgressSort}
            progressSortedData={progressSortedData}
            handleProgressEdit={handleProgressEdit}
            handleProgressDelete={handleProgressDelete}
            isLoading={progressLoading}
          />
        ) : null}

        {projectTypeVariant === 'fp' && canViewEffortEstimation ? (
          <EffortsSection
            expanded={expandedSections.efforts}
            onToggle={() => onToggleSection('efforts')}
            onDownload={handleEffortsDownload}
            onMoreMenuItems={effortsMoreMenuItems}
            onMore={handleEffortMore}
            showMoreAction={effortsMoreMenuItems.length > 0}
            showActions={canUpdateEffortEstimation || canDeleteEffortEstimation}
            canEditEffort={canUpdateEffortEstimation}
            canDeleteEffort={canDeleteEffortEstimation}
            effortsData={effortsData}
            expandedEffortRows={expandedEffortRows}
            toggleEffortRow={toggleEffortRow}
            handleEffortEdit={handleEffortEdit}
            handleEffortDelete={handleEffortDelete}
            isLoading={effortsLoading}
          />
        ) : null}

        {projectTypeVariant === 'tm' && projectUnitsData?.isApplicable && canViewProjectUnit ? (
          <ProjectUnitsSection
            expanded={expandedSections.projectUnits}
            onToggle={() => onToggleSection('projectUnits')}
            onDownload={handleProjectUnitsDownload}
            onMoreMenuItems={projectUnitsMoreMenuItems}
            onMore={handleProjectUnitMore}
            showMoreAction={projectUnitsMoreMenuItems.length > 0}
            projectUnitsData={projectUnitsData}
            onEditUnit={handleProjectUnitEdit}
            onDeleteUnit={handleProjectUnitDelete}
            canViewUnitAmount={canViewProjectUnitAmount}
            canEditUnit={canUpdateProjectUnit}
            canDeleteUnit={canDeleteProjectUnit}
            isLoading={projectUnitsLoading}
          />
        ) : null}

        {SHOW_REASONS_REMARKS_SECTION && canViewEffortEstimation ? (
          <ReasonsRemarksSection
            expanded={expandedSections.reasonsRemarks}
            onToggle={() => onToggleSection('reasonsRemarks')}
            primaryReason={reasonsRemarksForm.primaryReasonId}
            secondaryReason={reasonsRemarksForm.secondaryReasonId}
            remarks={reasonsRemarksForm.remarks}
            primaryReasonOptions={primaryReasonOptions}
            secondaryReasonOptions={secondaryReasonOptions}
            onPrimaryReasonChange={(value) => handleReasonsRemarksChange('primaryReasonId', value)}
            onSecondaryReasonChange={(value) => handleReasonsRemarksChange('secondaryReasonId', value)}
            onRemarksChange={(value) => handleReasonsRemarksChange('remarks', value)}
            onSave={handleReasonsRemarksSave}
            isSaving={isReasonsSaving}
            isLoading={isReasonsLoading}
            isDisabled={isReasonsLoading || !projectId}
          />
        ) : null}

        {canViewAuditScore ? (
          <AuditSection
            expanded={expandedSections.audit}
            onToggle={() => onToggleSection('audit')}
            onDownload={handleAuditDownload}
            onMore={handleAuditMore}
            onMoreMenuItems={auditMoreMenuItems}
            showMoreAction={canCreateAuditScore}
            showActions={canShowAuditActions}
            canEditAudit={canUpdateAuditScore}
            canDeleteAudit={canDeleteAuditScore}
            auditScoreData={auditScoreData}
            getAuditSortClass={getAuditSortClass}
            auditSort={auditSort}
            getAriaSort={getAriaSort}
            handleAuditSort={handleAuditSort}
            auditSortedData={auditSortedData}
            handleAuditEdit={handleAuditEdit}
            handleAuditDelete={handleAuditDelete}
            isAuditEmpty={isAuditEmpty}
            isLoading={auditLoading}
          />
        ) : null}

        {canViewAnyRiskRegister ? (
          <RiskRegisterSection
            sectionId="project-details-section-risk"
            expanded={expandedSections.risk}
            onToggle={() => onToggleSection('risk')}
            onDownload={handleRiskDownload}
            onMore={handleRiskMore}
            onMoreMenuItems={riskMoreMenuItems}
            showMoreAction={riskMoreMenuItems.length > 3}
            riskRegisterData={riskRegisterData}
            visibleTabs={riskVisibleTabs}
            activeRiskTab={activeRiskTab}
            setActiveRiskTab={setActiveRiskTab}
            activeRiskTabKey={activeRiskTabKey}
            summaryConfig={summaryConfig}
            riskRingData={riskRingData}
            isInfoSecTab={isInfoSecTab}
            issueRows={issueRows}
            expandedRiskRows={expandedRiskRows}
            toggleRiskRow={toggleRiskRow}
            canEditIssue={canUpdateIssue}
            canDeleteIssue={canDeleteIssue}
            handleIssueEdit={handleIssueEdit}
            handleIssueDelete={handleIssueDelete}
            visibleRiskRows={visibleRiskRows}
            canEditInfoSec={canUpdateInfoSecRisk}
            canEditRisk={canUpdateRisk}
            canDeleteInfoSec={canDeleteInfoSecRisk}
            canDeleteRisk={canDeleteRisk}
            handleInfoSecEdit={handleInfoSecEdit}
            handleRiskEdit={handleRiskEdit}
            handleInfoSecDelete={handleInfoSecDelete}
            handleRiskDelete={handleRiskDelete}
            isLoading={riskLoading}
          />
        ) : null}

        {canViewFinanceMilestone ? (
          <MilestoneSection
            expanded={expandedSections.milestone}
            onToggle={() => onToggleSection('milestone')}
            onDownload={handleMilestoneDownload}
            onMore={handleMilestoneMore}
            onMoreMenuItems={milestoneMoreMenuItems}
            showMoreAction={milestoneMoreMenuItems.length > 0}
            projectId={projectId}
            handleMilestoneEdit={handleMilestoneEdit}
            handleInvoiceAdd={handleInvoiceAdd}
            handleInvoiceEdit={handleInvoiceEdit}
            handleCollectionAdd={handleCollectionAdd}
            handleCollectionEdit={handleCollectionEdit}
          />
        ) : null}

        {canViewResourceAllocationCount ? (
          <ResourceCountSection
            expanded={expandedSections.resourceCount}
            onToggle={() => onToggleSection('resourceCount')}
            onDownload={handleResourceCountDownload}
            getResourceCountSortClass={getResourceCountSortClass}
            resourceCountSort={resourceCountSort}
            getAriaSort={getAriaSort}
            handleResourceCountSort={handleResourceCountSort}
            resourceCountSortedData={resourceCountSortedData}
            resourceCountTotals={resourceCountTotals}
            isLoading={resourceLoading}
          />
        ) : null}

        {canViewResourceAllocationDetails ? (
          <ResourceDetailsSection
            expanded={expandedSections.resourceDetails}
            onToggle={() => onToggleSection('resourceDetails')}
            onChangeEndDate={canUpdateResourceAllocationDetails ? handleResourceEndDateChange : undefined}
            onDownload={handleResourceDetailsDownload}
            showMoreAction={resourceDetailsMoreMenuItems.length > 0}
            onMoreMenuItems={resourceDetailsMoreMenuItems}
            resourceRingData={resourceRingData}
            resourceAllocationData={resourceAllocationData}
            getResourceSortClass={getResourceSortClass}
            resourceSort={resourceSort}
            getAriaSort={getAriaSort}
            handleResourceSort={handleResourceSort}
            resourceSortedData={resourceSortedData}
            canBulkChange={canUpdateResourceAllocationDetails}
            projectId={projectId}
            onRefreshData={onRefreshData}
            isLoading={resourceLoading}
          />
        ) : null}

        {canViewResourceAllocationDetails ? (
          <TbhResourcesSection
            expanded={expandedSections.tbhResources}
            onToggle={() => onToggleSection('tbhResources')}
            onDownload={handleTbhDownload}
            showMoreAction={tbhResourcesMoreMenuItems.length > 0}
            onMoreMenuItems={tbhResourcesMoreMenuItems}
            tbhResources={tbhResources}
            isAddTbhResourceOpen={isAddTbhResourceOpen}
            onCloseAddTbhResource={() => {
              setIsAddTbhResourceOpen(false);
              setEditingTbhAllocation(null);
            }}
            projectId={projectId}
            projectStatus={projectStatus}
            onRefreshData={onRefreshData}
            onEdit={(row) => {
              setEditingTbhAllocation(row);
              setIsAddTbhResourceOpen(true);
            }}
            allocationData={editingTbhAllocation}
            canUpdate={canUpdateResourceAllocationDetails}
            canDelete={canDeleteResourceAllocationDetails}
            isLoading={tbhLoading}
          />
        ) : null}

        <ProjectDetailsSectionModals
          modalState={modalState}
          closeModal={closeModal}
          csatCustomers={csatCustomers}
          csatCustomersLoading={csatCustomersLoading}
          csatCustomersError={csatCustomersError}
          submitCSATEdit={submitCSATEdit}
          confirmCSATDelete={confirmCSATDelete}
          isActionInProgress={isActionInProgress}
          onCSATHistoryClick={handleCSATHistoryRedirect}
          submitProgressEdit={submitProgressEdit}
          isProgressEditMode={isProgressEditMode}
          confirmProgressDelete={confirmProgressDelete}
          projectUnitsData={projectUnitsData}
          submitProjectUnitEdit={submitProjectUnitEdit}
          isProjectUnitEditMode={isProjectUnitEditMode}
          confirmProjectUnitDelete={confirmProjectUnitDelete}
          effortsData={effortsData}
          submitEffortEdit={submitEffortEdit}
          confirmEffortDelete={confirmEffortDelete}
          submitAuditEdit={submitAuditEdit}
          isAuditEditMode={isAuditEditMode}
          confirmAuditDelete={confirmAuditDelete}
          riskRegisterData={riskRegisterData}
          submitRiskEdit={submitRiskEdit}
          isRiskEditMode={isRiskEditMode}
          confirmRiskDelete={confirmRiskDelete}
          submitIssueEdit={submitIssueEdit}
          isIssueEditMode={isIssueEditMode}
          confirmIssueDelete={confirmIssueDelete}
          submitInfoSecEdit={submitInfoSecEdit}
          isInfoSecEditMode={isInfoSecEditMode}
          confirmInfoSecDelete={confirmInfoSecDelete}
          onMoreMenuItems={projectUnitsMoreMenuItems}
          onMore={handleProjectUnitMore}
          showMoreAction={projectUnitsMoreMenuItems.length > 0}
          onAddUnit={handleProjectUnitAdd}
          onEditUnit={handleProjectUnitEdit}
          onDeleteUnit={handleProjectUnitDelete}
          canViewUnitAmount={canViewProjectUnitAmount}
          canAddUnit={canCreateProjectUnit}
          canEditUnit={canUpdateProjectUnit}
          canDeleteUnit={canDeleteProjectUnit}
        />
      </div>
    </>
  );
};

ExpandableSections.propTypes = {
  expandedSections: PropTypes.shape({
    csat: PropTypes.bool.isRequired,
    progress: PropTypes.bool.isRequired,
    efforts: PropTypes.bool.isRequired,
    projectUnits: PropTypes.bool.isRequired,
    reasonsRemarks: PropTypes.bool.isRequired,
    audit: PropTypes.bool.isRequired,
    risk: PropTypes.bool.isRequired,
    milestone: PropTypes.bool.isRequired,
    resourceCount: PropTypes.bool.isRequired,
    resourceDetails: PropTypes.bool.isRequired,
    allocationDetails: PropTypes.bool.isRequired,
    tbhResources: PropTypes.bool.isRequired,
  }).isRequired,
  onToggleSection: PropTypes.func.isRequired,
  csatData: PropTypes.shape({
    score: PropTypes.number,
    date: PropTypes.string,
    overall: PropTypes.shape({
      total: PropTypes.number,
      average: PropTypes.number,
    }),
    currentYear: PropTypes.shape({
      total: PropTypes.number,
      average: PropTypes.number,
      year: PropTypes.string,
    }),
    lastReceived: PropTypes.string,
    pagination: PropTypes.shape({
      showing: PropTypes.string,
      total: PropTypes.string,
    }),
  }),
  csatTableData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      serviceQuality: PropTypes.number,
      roi: PropTypes.number,
      management: PropTypes.number,
      overall: PropTypes.number,
      customerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      customer: PropTypes.string,
      feedback: PropTypes.string,
    }),
  ),
  csatCustomers: PropTypes.arrayOf(
    PropTypes.shape({
      customer_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      customer_name: PropTypes.string,
    }),
  ),
  csatCustomersLoading: PropTypes.bool,
  csatCustomersError: PropTypes.string,
  lazyLoadCsat: PropTypes.func,
  progressData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      date: PropTypes.string,
      day: PropTypes.string,
      progress: PropTypes.number,
      remarks: PropTypes.string,
    }),
  ),
  effortsData: PropTypes.shape({
    rows: PropTypes.arrayOf(PropTypes.object),
    totals: PropTypes.object,
    total: PropTypes.number,
  }),
  projectUnitsData: PropTypes.shape({
    isApplicable: PropTypes.bool,
    rows: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      quantity: PropTypes.string,
      skills: PropTypes.string,
      resourceCategory: PropTypes.string,
      startDate: PropTypes.string,
      endDate: PropTypes.string,
      unitPrice: PropTypes.string,
      remarks: PropTypes.string,
    })),
  }),
  auditScoreData: PropTypes.shape({
    process: PropTypes.shape({
      count: PropTypes.number,
      average: PropTypes.number,
      lastAudit: PropTypes.number,
    }),
    quality: PropTypes.shape({
      count: PropTypes.number,
      average: PropTypes.number,
      lastAudit: PropTypes.number,
    }),
    technology: PropTypes.shape({
      count: PropTypes.number,
      average: PropTypes.number,
      lastAudit: PropTypes.number,
    }),
    history: PropTypes.arrayOf(PropTypes.object),
  }),
  riskRegisterData: PropTypes.shape({
    summary: PropTypes.object,
    tabs: PropTypes.arrayOf(PropTypes.string),
    risks: PropTypes.arrayOf(PropTypes.object),
    issues: PropTypes.arrayOf(PropTypes.object),
  }),
  lazyLoadRisk: PropTypes.func,
  milestoneData: PropTypes.shape({
    selectedIndex: PropTypes.number,
    summary: PropTypes.shape({
      total: PropTypes.number,
      completed: PropTypes.number,
      inProgress: PropTypes.number,
      delayed: PropTypes.number,
      planned: PropTypes.number,
      completionPercent: PropTypes.number,
    }),
    milestones: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        numericId: PropTypes.string,
        name: PropTypes.string,
        date: PropTypes.string,
        selected: PropTypes.bool,
        healthDots: PropTypes.arrayOf(PropTypes.string),
        owner: PropTypes.string,
        plannedDate: PropTypes.string,
        plannedDay: PropTypes.string,
        actualDate: PropTypes.string,
        actualDay: PropTypes.string,
        status: PropTypes.string,
        progress: PropTypes.number,
        remarks: PropTypes.string,
        poNumber: PropTypes.string,
        poLink: PropTypes.string,
        category: PropTypes.string,
        milestoneStatus: PropTypes.string,
        invoiceStatus: PropTypes.string,
        amount: PropTypes.number,
        dueDate: PropTypes.string,
        dueDateDay: PropTypes.string,
        replannedDate: PropTypes.string,
        replannedDateDay: PropTypes.string,
        invoiceDetails: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string,
            invoiceNumber: PropTypes.string,
            date: PropTypes.string,
            dateDay: PropTypes.string,
            amount: PropTypes.number,
            status: PropTypes.string,
            isCredit: PropTypes.bool,
          }),
        ),
      }),
    ),
  }),
  resourceCountData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      role: PropTypes.string,
      total: PropTypes.number,
      current: PropTypes.number,
      allocation: PropTypes.number,
    }),
  ),
  resourceAllocationData: PropTypes.shape({
    summary: PropTypes.object,
    allocations: PropTypes.arrayOf(PropTypes.object),
  }),
  tbhResources: PropTypes.array,
  projectTypeVariant: PropTypes.string,
  projectSeqId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  projectName: PropTypes.string,
  projectVertical: PropTypes.string,
  onRefreshData: PropTypes.func,
  projectStatus: PropTypes.string,
  resourceTypeOptions: PropTypes.array,
  employeeDirectory: PropTypes.array,
  progressLoading: PropTypes.bool,
  effortsLoading: PropTypes.bool,
  projectUnitsLoading: PropTypes.bool,
  auditLoading: PropTypes.bool,
  riskLoading: PropTypes.bool,
  resourceLoading: PropTypes.bool,
  tbhLoading: PropTypes.bool,
  projectData: PropTypes.object,
};

export default ExpandableSections;
